/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable no-bitwise */

import JSG from '@cedalo/jsg-ui';
import { NumberFormatter } from '@cedalo/number-format';
import { ChartExtensions, ToolbarExtensions } from '@cedalo/webui-extensions';
import {
	FormatAlignCenter,
	FormatAlignJustify,
	FormatAlignLeft,
	FormatAlignRight,
	VerticalAlignBottom,
	VerticalAlignCenter,
	VerticalAlignTop
} from '@mui/icons-material/esm';
import BorderAllIcon from '@mui/icons-material/esm/BorderAll';
import BorderBottomIcon from '@mui/icons-material/esm/BorderBottom';
import BorderClearIcon from '@mui/icons-material/esm/BorderClear';
import BorderHorizontalIcon from '@mui/icons-material/esm/BorderHorizontal';
import BorderInnerIcon from '@mui/icons-material/esm/BorderInner';
import BorderLeftIcon from '@mui/icons-material/esm/BorderLeft';
import BorderOuterIcon from '@mui/icons-material/esm/BorderOuter';
import BorderRightIcon from '@mui/icons-material/esm/BorderRight';
import BorderTopIcon from '@mui/icons-material/esm/BorderTop';
import BorderVerticalIcon from '@mui/icons-material/esm/BorderVertical';
import ToolRectangleIcon from '@mui/icons-material/esm/Crop32';
import BoldIcon from '@mui/icons-material/esm/FormatBold';
import ItalicIcon from '@mui/icons-material/esm/FormatItalic';
import ToolEllipseIcon from '@mui/icons-material/esm/PanoramaFishEye';
import RedoIcon from '@mui/icons-material/esm/Redo';
import ToolStar from '@mui/icons-material/esm/StarBorder';
import ToolPolylineIcon from '@mui/icons-material/esm/Timeline';
import ToolArrowIcon from '@mui/icons-material/esm/TrendingFlat';
import UndoIcon from '@mui/icons-material/esm/Undo';
import {
	AppBar,
	Autocomplete,
	Divider,
	IconButton,
	ImageList,
	ImageListItem,
	Input,
	ListItemText,
	MenuItem,
	MenuList,
	Popover,
	TextField,
	ToggleButton,
	ToggleButtonGroup
} from '@mui/material';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import SvgIcon from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import React, { useState } from 'react';
import { SketchPicker } from 'react-color';
import { FormattedMessage } from 'react-intl';
import { graphManager } from '../../GraphManager';
import { intl } from '../../helper/IntlGlobalProvider';
import CommandStack from '../../helper/synchronization/CommandStack';
import { numberFormatTemplates } from '../../languages/NumberFormatTemplates';
import CustomTooltip from '../utils/CustomTooltip';
import { useJSGNotification } from '../utils/JSGHooks';

const {
	Strings,
	Dictionary,
	BooleanAttribute,
	ItemAttributes,
	FormatAttributes,
	TextFormatAttributes,
	SheetPlotNode,
	SheetCommandFactory
} = JSG;

const chartButtonStyle = { height: '40px', width: '40px' };
const chartImageStyle = { height: '35px', width: '35px' };
const buttonStyle = { height: '34px', width: '30px', padding: '4px 0px 0px 0px' };
const borderStyle = { borderRadius: '0%', padding: '0px 5px', width: '100px', height: '20px' };

function ToolSeparator(props) {
	return (
		<div
			style={{
				borderLeft: '1px solid lightgrey',
				height: '40px',
				margin: props.left ? '0px 8px' : '0px 8px 0px 0px'
			}}
		/>
	);
}

const numberFormats = [
	{ id: 'NumberFormat.General', type: 'general', nf: 'General', local: 'general', value: 1234.56 },
	{ id: '-1' },
	{ id: 'NumberFormat.NumberRounded', type: 'number', nf: '#,##0', local: 'number;0;true', value: 1234.56 },
	{ id: 'NumberFormat.Number', type: 'number', nf: '#,##0.00', local: 'number;2;true', value: 1234.56 },
	{ id: 'NumberFormat.Percent', type: 'percent', nf: '0.00%', local: 'percent;2;false', value: 0.123 },
	{ id: 'NumberFormat.Currency', type: 'currency', nf: '#,##0.00 €', local: 'currency;2;true', value: 1234.56 },
	{ id: 'NumberFormat.Scientific', type: 'science', nf: '0.00E+00', local: 'science;2', value: 1234.56 },
	{ id: '-2' },
	{ id: 'NumberFormat.Date', type: 'date', nf: 'd\\.m\\.yy', local: 'date;de', value: 43500 },
	{ id: 'NumberFormat.Time', type: 'time', nf: 'h:mm:ss', local: 'time;de', value: 0.5 },
	{ id: 'NumberFormat.DateTime', type: 'date', nf: 'd\\.m\\.yyyy h:mm', local: 'date;de', value: 43500.5 },
	{ id: '-3' },
	{ id: 'NumberFormat.Text', type: 'text', nf: '@', local: 'text', value: 1234.56 },
	{ id: '-4' }
];

export default function SheetToolBar(props) {
	const [selectionInfo, setSelectionInfo] = useState({
		textFormat: {
			valign: 2,
			halign: 3,
			fontsize: 9,
			fontname: 'Verdana',
			fontcolor: JSG.theme.text,
			fontstyle: 0,
			numberformat: 'General',
			localculture: 'general'
		},
		format: {
			fillstyle: 0,
			fillcolor: JSG.theme.fill,
			leftbordercolor: JSG.theme.border,
			leftborderstyle: 0,
			leftborderwidth: 1,
			topbordercolor: JSG.theme.border,
			topborderstyle: 0,
			topborderwidth: 1,
			rightbordercolor: JSG.theme.border,
			rightborderstyle: 0,
			rightborderwidth: 1,
			bottombordercolor: JSG.theme.border,
			bottomborderstyle: 0,
			bottomborderwidth: 1
		},
		type: '',
		sheetView: undefined
	});
	// const [format, setFormat] = useState();
	// const [selectionType, setSelectionType] = useState('');
	// const [sheetView, setSheetView] = useState();
	const [anchorEl, setAnchorEl] = useState(null);
	const [canUndo, setCanUndo] = useState(false);
	const [canRedo, setCanRedo] = useState(false);
	const [popupId, setPopupId] = useState('');
	const theme = useTheme();
	const { appEditable } = props;

	const updateState = () => {
		const selection = graphManager.getGraphViewer().getSelection();
		const { graph } = graphManager;
		if (!graph) {
			return;
		}
		const selInfo = {...selectionInfo};

		if (selection.length) {
			if (selection[0].getModel() instanceof SheetPlotNode && selection[0].getView().chartSelection) {
				selInfo.textFormat = selection[0].getView().getSelectedTextFormat();
				selInfo.format = selection[0].getView().getSelectedFormat();
			} else {
				selInfo.textFormat = TextFormatAttributes.retainFromSelection(selection);
				selInfo.format = FormatAttributes.retainFromSelection(selection);
			}
			selInfo.type = 'shape';
			selInfo.sheetView = undefined;
		} else if (graph.sheetSelection.hasSelection) {
			const view = graphManager.getActiveSheetView();
			if (view) {
				const item = view.getItem();
				const sel = item.getOwnSelection();
				if (sel) {
					const cell = sel.getActiveCell();
					if (cell !== undefined) {
						selInfo.textFormat = item.model.getTextFormatProperties(cell.x, cell.y, true);
						selInfo.format = item.model.getFormatProperties(cell.x, cell.y, true);
					}
					selInfo.type = 'cell';
					selInfo.sheetView = view;
				}
			}
		} else {
			selInfo.type = '';
			selInfo.sheetView = undefined;
		}
		setSelectionInfo(selInfo);
	};

	const onSheetSelection = (notification) => {
		const { item } = notification.object;
		const { updateFinal } = notification.object;

		if (!item || !updateFinal) {
			return;
		}
		const activeCell = item.getOwnSelection().getActiveCell();
		if (!(item instanceof JSG.StreamSheet) || activeCell === undefined) {
			return;
		}

		updateState();
	};

	const onShapeSelection = () => {
		const selection = graphManager.getGraphViewer().getSelection();
		if (!selection.length) {
			return;
		}

		updateState();
	};

	const onCommandStack = (notification) => {
		const { commandStack } = notification.object;
		setCanUndo(commandStack.canUndo());
		setCanRedo(commandStack.canRedo());
	};

	const onCommand = () => {
		updateState();
	};
	// const onCommand = (notification) => {
	// 	const cmd = notification.object;
	// 	if (!(cmd instanceof SendSelectionCommand)) {
	// 		updateState();
	// 	}
	// };

	const onZoomUpdate = () => {
		updateState();
	};

	useJSGNotification(JSG.NotificationCenter.ZOOM_NOTIFICATION, onZoomUpdate);
	useJSGNotification(JSG.COMMAND_EXECUTED_NOTIFICATION, onCommand);
	useJSGNotification(CommandStack.STACK_CHANGED_NOTIFICATION, onCommandStack);
	useJSGNotification(JSG.WorksheetNode.SELECTION_CHANGED_NOTIFICATION, onSheetSelection);
	useJSGNotification(JSG.SelectionProvider.SELECTION_CHANGED_NOTIFICATION, onShapeSelection);

	// onTreeSelectionChanged() {
	// 	const graphView = graphManager.getGraphViewer().getGraphView();
	// 	const focus = graphView.getFocus();
	// 	const treeSelected = focus && focus.getView() instanceof JSG.TreeItemsView;
	//
	// 	if (state.treeSelected !== treeSelected) {
	// 		setState({ treeSelected });
	// 	}
	// }

	const onShowPopup = (event, id) => {
		// This prevents ghost click.
		event.preventDefault();
		setAnchorEl(event.currentTarget);
		setPopupId(id);
	};

	const onClosePopup = () => {
		setAnchorEl(null);
		setPopupId('');
	};

	const closePopup = () => {
		setAnchorEl(null);
		setPopupId('');
	};

	const onShowPredecessors = () => {
		closePopup();

		if (selectionInfo.sheetView) {
			selectionInfo.sheetView.getItem().collectPredecessors();
		}
		graphManager.redraw();
	};

	const onShowDependants = () => {
		closePopup();

		if (selectionInfo.sheetView) {
			selectionInfo.sheetView.getItem().collectDependants();
		}
		graphManager.redraw();
	};

	const onRemoveArrows = () => {
		closePopup();

		if (selectionInfo.sheetView) {
			selectionInfo.sheetView.getItem().removeArrows();
		}
		graphManager.redraw();
	};

	const isShapeSelected = () => {
		return graphManager.getGraphViewer().getSelectionProvider().hasSelection();
	};

	const executeCommand = (cmd) => {
		graphManager.getGraphViewer().getInteractionHandler().execute(cmd);
	};

	const applyFormatMap = (map) => {
		graphManager.getGraphEditor().getInteractionHandler().applyFormatMap(map);
	};

	const applyTextFormatMap = (map) => {
		graphManager.getGraphEditor().getInteractionHandler().applyTextFormatMap(map);
	};

	const setActiveInteraction = (interaction) => {
		graphManager.getGraphViewer().getInteractionHandler().setActiveInteraction(interaction);
	};

	const onCreateLine = () => {
		closePopup();
		const edge = new JSG.Edge(new JSG.LineShape());
		setActiveInteraction(new JSG.CreateEdgeInteraction(edge));
	};

	const onCreateArrow = () => {
		closePopup();
		const edge = new JSG.Edge(new JSG.LineShape());
		edge.getFormat().setLineArrowEnd(1);
		setActiveInteraction(new JSG.CreateEdgeInteraction(edge));
	};

	const onCreateRectangle = () => {
		closePopup();
		setActiveInteraction(new JSG.CreateNodeInteraction(new JSG.RectangleShape()));
	};

	const onCreateEllipse = () => {
		closePopup();

		const node = new JSG.Node(new JSG.EllipseShape());
		setActiveInteraction(new JSG.CreateItemInteraction(node));
	};

	const onCreateText = () => {
		closePopup();

		const textnode = new JSG.TextNode('Text');
		const f = textnode.getTextFormat();
		f.setHorizontalAlignment(TextFormatAttributes.TextAlignment.LEFT);
		f.setVerticalAlignment(TextFormatAttributes.VerticalTextAlignment.TOP);
		f.setRichText(false);
		textnode.associate(false);

		setActiveInteraction(new JSG.CreateItemInteraction(textnode));
	};

	const onCreatePolyline = () => {
		closePopup();
		const polynode = new JSG.Node(new JSG.PolygonShape());
		polynode.setItemAttribute(ItemAttributes.CLOSED, false);
		setActiveInteraction(new JSG.CreatePolyLineInteraction(polynode));
	};

	const onCreateTool = (type) => {
		closePopup();
		setActiveInteraction(new JSG.CreateItemInteraction(JSG.graphItemFactory.createItemFromString(type)));
	};

	const onSelectShapes = () => {
		const viewer = graphManager.getGraphViewer();
		viewer.clearSelection();
		viewer.getInteractionHandler().setActiveInteraction(new JSG.MarqueeInteraction(true));
	};

	const onUndo = () => {
		graphManager.undo();
		graphManager.getCanvas().focus();
	};

	const onRedo = () => {
		graphManager.redo();
		graphManager.getCanvas().focus();
	};

	const onCreateControl = (type) => {
		let node;

		closePopup();

		switch (type) {
			case 'layout':
				node = new JSG.LayoutNode();
				break;
			case 'button':
				node = new JSG.SheetButtonNode();
				break;
			case 'checkbox':
				node = new JSG.SheetCheckboxNode();
				break;
			case 'json':
				node = new JSG.SheetJsonNode();
				break;
			case 'slider':
				node = new JSG.SheetSliderNode();
				break;
			case 'knob':
				node = new JSG.SheetKnobNode();
				break;
			default:
				break;
		}

		setActiveInteraction(new JSG.CreateItemInteraction(node));
		graphManager.getCanvas().focus();
	};

	const isChartSelected = () => {
		const selection = graphManager.getGraphViewer().getSelection();
		return selection && selection.length > 0 && selection[0].getModel() instanceof SheetPlotNode;
	};

	const isChartElementSelected = () => {
		const selection = graphManager.getGraphViewer().getSelection();
		if (selection && selection.length) {
			const cont = selection[0];
			if (cont.getModel() instanceof SheetPlotNode) {
				return cont.getView().chartSelection !== undefined;
			}
		}
		return false;
	};

	const isChartElementWithoutFontSelected = () => {
		const selection = graphManager.getGraphViewer().getSelection();
		if (selection && selection.length) {
			const cont = selection[0];
			if (cont.getModel() instanceof SheetPlotNode) {
				const sel = cont.getView().chartSelection;
				if (!sel) {
					return true;
				}
				return sel.element === 'point' || (sel.element === 'series' && !cont.getModel().isPie());
			}
		}
		return false;
	};

	const onCreatePlot = (type) => {
		closePopup();

		if (isChartSelected()) {
			const selection = graphManager.getGraphViewer().getSelection();
			const item = selection[0].getModel();
			const cmd = new JSG.CompoundCommand();
			const cmdAxis = item.prepareCommand('axes');
			const cmdChart = item.prepareCommand('chart');
			const cmdSeries = item.prepareCommand('series');

			type = item.setChartType(type);

			item.series.forEach((serie) => {
				serie.type = type;
			});

			item.setChartTypeForSeries(type);

			item.finishCommand(cmdSeries, 'series');
			item.finishCommand(cmdChart, 'chart');
			item.finishCommand(cmdAxis, 'axes');
			cmd.add(cmdChart);
			cmd.add(cmdAxis);
			cmd.add(cmdSeries);
			executeCommand(cmd);
		} else {
			const node = new JSG.SheetPlotNode();
			const attr = node.addAttribute(new BooleanAttribute('showwizard', true));
			attr.setTransient(true);
			graphManager.chartType = type;
			if (selectionInfo.sheetView) {
				const selection = selectionInfo.sheetView.getOwnSelection();
				if (selection) {
					graphManager.chartSelection = selection.copy();
				}
			}

			setActiveInteraction(new JSG.CreateItemInteraction(node));
		}
		graphManager.getCanvas().focus();
	};

	const onFormatHorizontalAlign = (align) => {
		const attributesMap = new Dictionary();

		attributesMap.put(TextFormatAttributes.HORIZONTALALIGN, align);

		if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		}
		closePopup();
		graphManager.getCanvas().focus();
	};

	const onFormatVerticalAlign = (align) => {
		const attributesMap = new Dictionary();

		attributesMap.put(TextFormatAttributes.VERTICALALIGN, align);

		if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		}
		closePopup();
	};

	const onFormatFontSize = (event, value) => {
		const attributesMap = new Dictionary();

		attributesMap.put(TextFormatAttributes.FONTSIZE, Number(value));

		if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		}
		graphManager.getCanvas().focus();
	};

	const onFormatFontName = (event) => {
		const attributesMap = new Dictionary();

		attributesMap.put(TextFormatAttributes.FONTNAME, event.target.value);

		if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		}
		graphManager.getCanvas().focus();
	};

	const onFormatPercent = () => {
		const attributesMap = new Dictionary();

		attributesMap.put(TextFormatAttributes.NUMBERFORMAT, '0%');
		attributesMap.put(TextFormatAttributes.LOCALCULTURE, 'percent;0');

		if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		} else if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		}
		graphManager.getCanvas().focus();
	};

	const onFormatNumber = () => {
		const attributesMap = new Dictionary();
		const template = numberFormatTemplates.getNegativeNumberTemplates(true, 2, 'red', 0, 0, undefined, 0);
		const culture = `number;2;true`;

		attributesMap.put(TextFormatAttributes.NUMBERFORMAT, template.format);
		attributesMap.put(TextFormatAttributes.LOCALCULTURE, culture);

		if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		} else if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		}
		graphManager.getCanvas().focus();
	};

	const isNumberFormatAvailable = () => {
		if (selectionInfo.type === 'cell') {
			return true;
		} else if (selectionInfo.type === 'shape') {
			const selection = graphManager.getGraphViewer().getSelection();
			if (selection && selection.length) {
				const cont = selection[0];
				if (cont.getModel() instanceof SheetPlotNode) {
					const sel = cont.getView().chartSelection;
					if (!sel) {
						return false;
					}
					return sel.element === 'serieslabel' || sel.element === 'xAxis' || sel.element === 'yAxis';
				} else if (cont.getModel() instanceof JSG.TextNode) {
					return true;
				}
			}
			return false;
		}

		return false;
	};

	const onFormatNumberFormat = (fmt) => {
		const attributesMap = new Dictionary();

		attributesMap.put(TextFormatAttributes.NUMBERFORMAT, fmt.nf);
		attributesMap.put(TextFormatAttributes.LOCALCULTURE, fmt.local);

		if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		}

		closePopup();
		graphManager.getCanvas().focus();
	};

	const onFormatDecimals = (more, thousandsFlag) => {
		const attributesMap = new Dictionary();
		const tf = selectionInfo.textFormat;
		if (!tf) {
			return;
		}

		let decimals = 0;
		let category = 'number';
		let currency = '€';
		let thousands = false;
		const sections = tf.localculture.split(';');

		if (sections.length) {
			switch (sections[0]) {
				case 'general':
					category = 'number';
					break;
				case 'currency':
					[, , , currency] = sections;
					currency = currency === 'undefined' ? '€' : currency;
					decimals = Number(sections[1]);
					[category] = sections;
					thousands = sections[2] === 'true';
					break;
				case 'number':
					decimals = Number(sections[1]);
					[category] = sections;
					thousands = sections[2] === 'true';
					break;
				case 'percent':
				case 'science':
					decimals = Number(sections[1]);
					[category] = sections;
					break;
				default:
					category = 'number';
					break;
			}
		}

		if (decimals !== undefined) {
			decimals = Math.max(0, more ? decimals + 1 : decimals - 1);
		}
		if (thousandsFlag !== undefined) {
			thousands = true;
		}

		let template;
		let culture;

		switch (category) {
			default:
			case 'number':
				template = numberFormatTemplates.getNegativeNumberTemplates(
					thousands,
					Number(decimals),
					'red',
					0,
					0,
					undefined,
					0
				);
				culture = `number;${decimals};${thousands}`;
				break;
			case 'currency':
				template = numberFormatTemplates.getNegativeNumberTemplates(
					thousands,
					Number(decimals),
					'red',
					0,
					0,
					currency,
					0
				);
				culture = `currency;${decimals};${thousands};${currency}`;
				break;
			case 'percent':
				template = numberFormatTemplates.getNumberTemplate(Number(decimals), 0, 5, 0);
				culture = `percent;${decimals}`;
				break;
			case 'science':
				template = numberFormatTemplates.getNumberTemplate(Number(decimals), 0, 7, 0);
				culture = `science;${decimals}`;
				break;
		}

		attributesMap.put(TextFormatAttributes.NUMBERFORMAT, template.format);
		attributesMap.put(TextFormatAttributes.LOCALCULTURE, culture);

		if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		} else {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		}

		graphManager.getCanvas().focus();
	};

	const onFormatBold = () => {
		const attributesMap = new Dictionary();
		const tf = selectionInfo.textFormat;
		if (!tf) {
			return;
		}

		const style = tf.fontstyle;
		const newStyle =
			style & TextFormatAttributes.FontStyle.BOLD
				? style & ~TextFormatAttributes.FontStyle.BOLD
				: style | TextFormatAttributes.FontStyle.BOLD;

		attributesMap.put(TextFormatAttributes.FONTSTYLE, newStyle);

		if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		}
		graphManager.getCanvas().focus();
	};

	const onFormatItalic = () => {
		const attributesMap = new Dictionary();
		const tf = selectionInfo.textFormat;

		if (!tf) {
			return;
		}

		const style = tf.fontstyle;
		const newStyle =
			style & TextFormatAttributes.FontStyle.ITALIC
				? style & ~TextFormatAttributes.FontStyle.ITALIC
				: style | TextFormatAttributes.FontStyle.ITALIC;

		attributesMap.put(TextFormatAttributes.FONTSTYLE, newStyle);

		if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		}

		graphManager.getCanvas().focus();
	};

	const onFormatFillColor = (color, event) => {
		const attributesMap = new Dictionary();
		if (color.hex === 'transparent') {
			attributesMap.put(FormatAttributes.FILLSTYLE, FormatAttributes.FillStyle.NONE);
		} else if (color.hex.toUpperCase() === '#FFFFFE') {
			attributesMap.put(FormatAttributes.FILLCOLOR, 'auto');
			const f = selectionInfo.format;
			const style = f && f.fillstyle ? f.fillstyle : '';
			if (style === FormatAttributes.FillStyle.NONE) {
				attributesMap.put(FormatAttributes.FILLSTYLE, FormatAttributes.FillStyle.SOLID);
			}
		} else {
			attributesMap.put(FormatAttributes.FILLCOLOR, color.hex);
			attributesMap.put(FormatAttributes.FILLSTYLE, FormatAttributes.FillStyle.SOLID);
		}
		if (isChartSelected()) {
			attributesMap.put(FormatAttributes.TRANSPARENCY, color.rgb.a * 100);
		}

		if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.FormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			applyFormatMap(attributesMap);
		}

		if (event.target && event.target.style.cursor === 'pointer') {
			closePopup();
		}
		// graphManager.getCanvas().focus();
	};

	const onFormatBorderStyle = (style) => {
		let cmd;
		const attributesMap = new Dictionary();

		if (selectionInfo.sheetView) {
			const selection = selectionInfo.sheetView.getOwnSelection();
			attributesMap.put('leftborderstyle', style);
			attributesMap.put('topborderstyle', style);
			attributesMap.put('rightborderstyle', style);
			attributesMap.put('bottomborderstyle', style);
			cmd = SheetCommandFactory.create('command.FormatCellsCommand', selection.getRanges(), attributesMap);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			attributesMap.put(FormatAttributes.LINESTYLE, style);
			applyFormatMap(attributesMap);
		}

		graphManager.getCanvas().focus();
		closePopup();
	};

	const onFormatBorderWidth = (width) => {
		let cmd;
		const attributesMap = new Dictionary();

		if (selectionInfo.sheetView) {
			const selection = selectionInfo.sheetView.getOwnSelection();
			switch (width) {
				case -1:
					width = 1;
					break;
				case 25:
					width = 1.5;
					break;
				case 50:
					width = 2;
					break;
				case 75:
					width = 3;
					break;
				case 100:
					width = 4;
					break;
				case 200:
					width = 8;
					break;
				case 300:
					width = 12;
					break;
				case 400:
					width = 16;
					break;
				default:
					break;
			}
			attributesMap.put('leftborderwidth', width);
			attributesMap.put('topborderwidth', width);
			attributesMap.put('rightborderwidth', width);
			attributesMap.put('bottomborderwidth', width);
			cmd = SheetCommandFactory.create('command.FormatCellsCommand', selection.getRanges(), attributesMap);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			attributesMap.put(FormatAttributes.LINEWIDTH, width);
			applyFormatMap(attributesMap);
		}

		graphManager.getCanvas().focus();
		closePopup();
	};

	// const formatOuterFrame = (cmd, selection, style, width, color, direction) => {
	// 	const attributesMap = new Dictionary();
	// 	let rangeCopy;

	// 	selection.getRanges().forEach((range) => {
	// 		if (direction === 'all' || direction === 'top') {
	// 			if (range._y1) {
	// 				attributesMap.clear();
	// 				attributesMap.put('bottomborderstyle', style);
	// 				attributesMap.put('bottomborderwidth', width);
	// 				if (color !== undefined) {
	// 					attributesMap.put('bottombordercolor', color);
	// 				}

	// 				rangeCopy = range.copy();
	// 				rangeCopy._y1 -= 1;
	// 				rangeCopy._y2 = rangeCopy._y1;
	// 				cmd.add(SheetCommandFactory.create('command.FormatCellsCommand', [rangeCopy], attributesMap));
	// 			}
	// 		}
	// 		if (direction === 'all' || direction === 'bottom') {
	// 			if (range._y2 < range.sheet.maxRows) {
	// 				attributesMap.clear();
	// 				attributesMap.put('topborderstyle', style);
	// 				attributesMap.put('topborderwidth', width);
	// 				if (color !== undefined) {
	// 					attributesMap.put('topbordercolor', color);
	// 				}

	// 				rangeCopy = range.copy();
	// 				rangeCopy._y1 = rangeCopy._y2 + 1;
	// 				rangeCopy._y2 = rangeCopy._y1;
	// 				cmd.add(SheetCommandFactory.create('command.FormatCellsCommand', [rangeCopy], attributesMap));
	// 			}
	// 		}
	// 		if (direction === 'all' || direction === 'left') {
	// 			if (range._x1) {
	// 				attributesMap.clear();
	// 				attributesMap.put('rightborderstyle', style);
	// 				attributesMap.put('rightborderwidth', width);
	// 				if (color !== undefined) {
	// 					attributesMap.put('rightbordercolor', color);
	// 				}

	// 				rangeCopy = range.copy();
	// 				rangeCopy._x1 -= 1;
	// 				rangeCopy._x2 = rangeCopy._x1;
	// 				cmd.add(SheetCommandFactory.create('command.FormatCellsCommand', [rangeCopy], attributesMap));
	// 			}
	// 		}
	// 		if (direction === 'all' || direction === 'right') {
	// 			if (range._x2 < range.sheet.maxColumns) {
	// 				attributesMap.clear();
	// 				attributesMap.put('leftborderstyle', style);
	// 				attributesMap.put('leftborderwidth', width);
	// 				if (color !== undefined) {
	// 					attributesMap.put('leftbordercolor', color);
	// 				}

	// 				rangeCopy = range.copy();
	// 				rangeCopy._x1 = rangeCopy._x2 + 1;
	// 				rangeCopy._x2 = rangeCopy._x1;
	// 				cmd.add(SheetCommandFactory.create('command.FormatCellsCommand', [rangeCopy], attributesMap));
	// 			}
	// 		}
	// 	});
	// };

	const onFormatBorder = (type) => {
		if (selectionInfo.sheetView) {
			const attributesMap = new Dictionary();

			if (graphManager.getGraphEditor().getSelectionProvider().hasSelection()) {
				// graphManager.getGraphEditor().getInteractionHandler().applyFormatMap(attributesMap);
			} else {
				let selection = selectionInfo.sheetView.getOwnSelection();
				let cmd;
				let rangeCopy;

				switch (type) {
					case 'all':
						attributesMap.put('leftborderstyle', FormatAttributes.LineStyle.SOLID);
						attributesMap.put('topborderstyle', FormatAttributes.LineStyle.SOLID);
						attributesMap.put('rightborderstyle', FormatAttributes.LineStyle.SOLID);
						attributesMap.put('bottomborderstyle', FormatAttributes.LineStyle.SOLID);
						attributesMap.put('leftborderwidth', 1);
						attributesMap.put('topborderwidth', 1);
						attributesMap.put('rightborderwidth', 1);
						attributesMap.put('bottomborderwidth', 1);
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						// formatOuterFrame(cmd, selection, FormatAttributes.LineStyle.SOLID, 1, undefined, 'all');
						break;
					case 'clear':
						attributesMap.put('leftborderstyle', FormatAttributes.LineStyle.NONE);
						attributesMap.put('topborderstyle', FormatAttributes.LineStyle.NONE);
						attributesMap.put('rightborderstyle', FormatAttributes.LineStyle.NONE);
						attributesMap.put('bottomborderstyle', FormatAttributes.LineStyle.NONE);
						attributesMap.put('leftborderwidth', 1);
						attributesMap.put('topborderwidth', 1);
						attributesMap.put('rightborderwidth', 1);
						attributesMap.put('bottomborderwidth', 1);
						attributesMap.put('leftbordercolor', JSG.theme.border);
						attributesMap.put('topbordercolor', JSG.theme.border);
						attributesMap.put('rightbordercolor', JSG.theme.border);
						attributesMap.put('bottombordercolor', JSG.theme.border);
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						// formatOuterFrame(cmd, selection, FormatAttributes.LineStyle.NONE, 1, JSG.theme.border, 'all');
						break;
					case 'left':
						attributesMap.put('leftborderstyle', FormatAttributes.LineStyle.SOLID);
						attributesMap.put('leftborderwidth', 1);
						selection = selectionInfo.sheetView.getOwnSelection().copy();
						selection.getRanges().forEach((range) => {
							range.setWidth(1);
						});
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						// formatOuterFrame(cmd, selection, FormatAttributes.LineStyle.SOLID, 1, undefined, 'left');
						break;
					case 'top':
						attributesMap.put('topborderstyle', FormatAttributes.LineStyle.SOLID);
						attributesMap.put('topborderwidth', 1);
						selection = selectionInfo.sheetView.getOwnSelection().copy();
						selection.getRanges().forEach((range) => {
							range.setHeight(1);
						});
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						// formatOuterFrame(cmd, selection, FormatAttributes.LineStyle.SOLID, 1, undefined, 'top');
						break;
					case 'right':
						attributesMap.put('rightborderstyle', FormatAttributes.LineStyle.SOLID);
						attributesMap.put('rightborderwidth', 1);
						selection = selectionInfo.sheetView.getOwnSelection().copy();
						selection.getRanges().forEach((range) => {
							range._x1 = range._x2;
						});
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						// formatOuterFrame(cmd, selection, FormatAttributes.LineStyle.SOLID, 1, undefined, 'right');
						break;
					case 'bottom':
						attributesMap.put('bottomborderstyle', FormatAttributes.LineStyle.SOLID);
						attributesMap.put('bottomborderwidth', 1);
						selection = selectionInfo.sheetView.getOwnSelection().copy();
						selection.getRanges().forEach((range) => {
							range._y1 = range._y2;
						});
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						// formatOuterFrame(cmd, selection, FormatAttributes.LineStyle.SOLID, 1, undefined, 'bottom');
						break;
					case 'bottomfat':
						attributesMap.put('bottomborderstyle', FormatAttributes.LineStyle.SOLID);
						attributesMap.put('bottomborderwidth', 3);
						selection = selectionInfo.sheetView.getOwnSelection().copy();
						selection.getRanges().forEach((range) => {
							range._y1 = range._y2;
						});
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						break;
					case 'inner': {
						let copyRanges;
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						selection.getRanges().forEach((range) => {
							if (range.getWidth() > 1) {
								attributesMap.clear();
								attributesMap.put('rightborderstyle', FormatAttributes.LineStyle.SOLID);
								rangeCopy = range.copy();
								rangeCopy.setWidth(1);
								copyRanges = [];
								copyRanges.push(rangeCopy);
								cmd.add(
									SheetCommandFactory.create('command.FormatCellsCommand', copyRanges, attributesMap)
								);

								if (range.getWidth() > 2) {
									attributesMap.clear();
									attributesMap.put('leftborderstyle', FormatAttributes.LineStyle.SOLID);
									attributesMap.put('rightborderstyle', FormatAttributes.LineStyle.SOLID);

									rangeCopy = range.copy();
									rangeCopy._x1 += 1;
									rangeCopy._x2 -= 1;
									copyRanges = [];
									copyRanges.push(rangeCopy);
									cmd.add(
										SheetCommandFactory.create(
											'command.FormatCellsCommand',
											copyRanges,
											attributesMap
										)
									);
								}

								attributesMap.clear();
								attributesMap.put('leftborderstyle', FormatAttributes.LineStyle.SOLID);
								rangeCopy = range.copy();
								rangeCopy._x1 = rangeCopy._x2;
								copyRanges = [];
								copyRanges.push(rangeCopy);
								cmd.add(
									SheetCommandFactory.create('command.FormatCellsCommand', copyRanges, attributesMap)
								);
							}
						});

						selection.getRanges().forEach((range) => {
							if (range.getHeight() > 1) {
								attributesMap.clear();
								attributesMap.put('bottomborderstyle', FormatAttributes.LineStyle.SOLID);
								rangeCopy = range.copy();
								rangeCopy.setHeight(1);
								copyRanges = [];
								copyRanges.push(rangeCopy);
								cmd.add(
									SheetCommandFactory.create('command.FormatCellsCommand', copyRanges, attributesMap)
								);

								if (range.getHeight() > 2) {
									attributesMap.clear();
									attributesMap.put('topborderstyle', FormatAttributes.LineStyle.SOLID);
									attributesMap.put('bottomborderstyle', FormatAttributes.LineStyle.SOLID);

									rangeCopy = range.copy();
									rangeCopy._y1 += 1;
									rangeCopy._y2 -= 1;
									copyRanges = [];
									copyRanges.push(rangeCopy);
									cmd.add(
										SheetCommandFactory.create(
											'command.FormatCellsCommand',
											copyRanges,
											attributesMap
										)
									);
								}

								attributesMap.clear();
								attributesMap.put('topborderstyle', FormatAttributes.LineStyle.SOLID);
								rangeCopy = range.copy();
								rangeCopy._y1 = rangeCopy._y2;
								copyRanges = [];
								copyRanges.push(rangeCopy);
								cmd.add(
									SheetCommandFactory.create('command.FormatCellsCommand', copyRanges, attributesMap)
								);
							}
						});
						// formatOuterFrame(cmd, selection, FormatAttributes.LineStyle.NONE, 1, JSG.theme.border, 'all');
						if (!cmd.hasCommands()) {
							cmd = undefined;
						}
						break;
					}
					case 'outer':
					case 'outerfat':
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						attributesMap.put('leftborderstyle', FormatAttributes.LineStyle.SOLID);
						if (type === 'outerfat') {
							attributesMap.put('leftborderwidth', 3);
						}
						selection = selectionInfo.sheetView.getOwnSelection().copy();
						selection.getRanges().forEach((range) => {
							range.setWidth(1);
						});
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						attributesMap.clear();
						attributesMap.put('topborderstyle', FormatAttributes.LineStyle.SOLID);
						if (type === 'outerfat') {
							attributesMap.put('topborderwidth', 3);
						}
						selection = selectionInfo.sheetView.getOwnSelection().copy();
						selection.getRanges().forEach((range) => {
							range.setHeight(1);
						});
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						attributesMap.clear();
						attributesMap.put('rightborderstyle', FormatAttributes.LineStyle.SOLID);
						if (type === 'outerfat') {
							attributesMap.put('rightborderwidth', 3);
						}
						selection = selectionInfo.sheetView.getOwnSelection().copy();
						selection.getRanges().forEach((range) => {
							range._x1 = range._x2;
						});
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						attributesMap.clear();
						attributesMap.put('bottomborderstyle', FormatAttributes.LineStyle.SOLID);
						if (type === 'outerfat') {
							attributesMap.put('bottomborderwidth', 3);
						}
						selection = selectionInfo.sheetView.getOwnSelection().copy();
						selection.getRanges().forEach((range) => {
							range._y1 = range._y2;
						});
						cmd.add(
							SheetCommandFactory.create(
								'command.FormatCellsCommand',
								selection.getRanges(),
								attributesMap
							)
						);
						// formatOuterFrame(
						// 	cmd,
						// 	selectionInfo.sheetView.getOwnSelection(),
						// 	FormatAttributes.LineStyle.SOLID,
						// 	type === 'outerfat' ? 3 : 1,
						// 	undefined,
						// 	'all'
						// );
						break;
					case 'vertical': {
						let copyRanges;
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						selection.getRanges().forEach((range) => {
							if (range.getWidth() > 1) {
								attributesMap.clear();
								attributesMap.put('rightborderstyle', FormatAttributes.LineStyle.SOLID);
								rangeCopy = range.copy();
								rangeCopy.setWidth(1);
								copyRanges = [];
								copyRanges.push(rangeCopy);
								cmd.add(
									SheetCommandFactory.create('command.FormatCellsCommand', copyRanges, attributesMap)
								);

								if (range.getWidth() > 2) {
									attributesMap.clear();
									attributesMap.put('leftborderstyle', FormatAttributes.LineStyle.SOLID);
									attributesMap.put('rightborderstyle', FormatAttributes.LineStyle.SOLID);

									rangeCopy = range.copy();
									rangeCopy._x1 += 1;
									rangeCopy._x2 -= 1;
									copyRanges = [];
									copyRanges.push(rangeCopy);
									cmd.add(
										SheetCommandFactory.create(
											'command.FormatCellsCommand',
											copyRanges,
											attributesMap
										)
									);
								}

								attributesMap.clear();
								attributesMap.put('leftborderstyle', FormatAttributes.LineStyle.SOLID);
								rangeCopy = range.copy();
								rangeCopy._x1 = rangeCopy._x2;
								copyRanges = [];
								copyRanges.push(rangeCopy);
								cmd.add(
									SheetCommandFactory.create('command.FormatCellsCommand', copyRanges, attributesMap)
								);
							}
						});
						// formatOuterFrame(cmd, selection, FormatAttributes.LineStyle.NONE, 1, JSG.theme.border, 'all');
						if (!cmd.hasCommands()) {
							cmd = undefined;
						}
						break;
					}
					case 'horizontal': {
						let copyRanges;
						cmd = SheetCommandFactory.create('command.CompoundCommand', 'formats');
						selection.getRanges().forEach((range) => {
							if (range.getHeight() > 1) {
								attributesMap.clear();
								attributesMap.put('bottomborderstyle', FormatAttributes.LineStyle.SOLID);
								rangeCopy = range.copy();
								rangeCopy.setHeight(1);
								copyRanges = [];
								copyRanges.push(rangeCopy);
								cmd.add(
									SheetCommandFactory.create('command.FormatCellsCommand', copyRanges, attributesMap)
								);

								if (range.getHeight() > 2) {
									attributesMap.clear();
									attributesMap.put('topborderstyle', FormatAttributes.LineStyle.SOLID);
									attributesMap.put('bottomborderstyle', FormatAttributes.LineStyle.SOLID);

									rangeCopy = range.copy();
									rangeCopy._y1 += 1;
									rangeCopy._y2 -= 1;
									copyRanges = [];
									copyRanges.push(rangeCopy);
									cmd.add(
										SheetCommandFactory.create(
											'command.FormatCellsCommand',
											copyRanges,
											attributesMap
										)
									);
								}

								attributesMap.clear();
								attributesMap.put('topborderstyle', FormatAttributes.LineStyle.SOLID);
								rangeCopy = range.copy();
								rangeCopy._y1 = rangeCopy._y2;
								copyRanges = [];
								copyRanges.push(rangeCopy);
								cmd.add(
									SheetCommandFactory.create('command.FormatCellsCommand', copyRanges, attributesMap)
								);
							}
						});
						// formatOuterFrame(cmd, selection, FormatAttributes.LineStyle.NONE, 1, JSG.theme.border, 'all');
						if (!cmd.hasCommands()) {
							cmd = undefined;
						}
						break;
					}
					default:
						return;
				}

				if (cmd) {
					cmd = SheetCommandFactory.reduce(cmd);
					graphManager.getGraphViewer().getInteractionHandler().execute(cmd);
				}
			}
			graphManager.getCanvas().focus();
			closePopup();
		}
	};

	const onFormatFontColor = (color, event) => {
		const attributesMap = new Dictionary();
		attributesMap.put(TextFormatAttributes.FONTCOLOR, color.hex);

		if (selectionInfo.sheetView) {
			const cmd = SheetCommandFactory.create(
				'command.TextFormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			applyTextFormatMap(attributesMap);
		}
		graphManager.getCanvas().focus();

		if (event.target && event.target.style.cursor === 'pointer') {
			closePopup();
		}
	};

	const onFormatBorderColor = (color, event) => {
		const attributesMap = new Dictionary();

		if (selectionInfo.sheetView) {
			if (color.hex === 'transparent') {
				onFormatBorder('clear');
				return;
			}

			attributesMap.put('leftbordercolor', color.hex);
			attributesMap.put('topbordercolor', color.hex);
			attributesMap.put('rightbordercolor', color.hex);
			attributesMap.put('bottombordercolor', color.hex);

			const cmd = SheetCommandFactory.create(
				'command.FormatCellsCommand',
				selectionInfo.sheetView.getOwnSelection().getRanges(),
				attributesMap
			);
			executeCommand(cmd);
		} else if (isShapeSelected()) {
			if (color.hex === 'transparent') {
				attributesMap.put(FormatAttributes.LINESTYLE, FormatAttributes.LineStyle.NONE);
			} else if (color.hex.toUpperCase() === '#FFFFFE') {
				attributesMap.put(FormatAttributes.LINECOLOR, 'auto');
				const f = selectionInfo.format;
				const style = f && f.linestyle;
				if (style === FormatAttributes.LineStyle.NONE) {
					attributesMap.put(FormatAttributes.LINESTYLE, FormatAttributes.LineStyle.SOLID);
				}
			} else {
				attributesMap.put(FormatAttributes.LINECOLOR, color.hex);
				const f = selectionInfo.format;
				const style = f && f.linestyle;
				if (style === FormatAttributes.LineStyle.NONE) {
					attributesMap.put(FormatAttributes.LINESTYLE, FormatAttributes.LineStyle.SOLID);
				}
			}
			applyFormatMap(attributesMap);
		}

		graphManager.getCanvas().focus();

		if (event.target && event.target.style.cursor === 'pointer') {
			closePopup();
		}
	};

	const onSplitView = () => {
		const graph = graphManager.graph;
		graph.machineContainer.splitView = !graph.machineContainer.splitView;
		props.onSplitViewChange();
	};

	const getFormatBorderColor = () => {
		if (selectionInfo.sheetView) {
			const attr = selectionInfo.format;
			if (attr) {
				let lcolor = attr.leftbordercolor;
				const tcolor = attr.topbordercolor;
				const rcolor = attr.rightbordercolor;
				const bcolor = attr.bottombordercolor;
				if (lcolor !== rcolor || lcolor !== tcolor || lcolor !== bcolor) {
					lcolor = '';
				}
				return lcolor;
			}
		} else if (
			graphManager.getGraphEditor() &&
			graphManager.getGraphEditor().getGraphViewer() &&
			isShapeSelected()
		) {
			const f = selectionInfo.format;
			return f && f.linecolor;
		}

		return '';
	};

	const getFormatBorderStyle = () => {
		if (selectionInfo.sheetView) {
			const attr = selectionInfo.format;
			if (attr) {
				let lStyle = attr.leftborderstyle;
				const tStyle = attr.topborderstyle;
				const rStyle = attr.rightborderstyle;
				const bStyle = attr.bottomborderstyle;
				if (lStyle !== rStyle || lStyle !== tStyle || lStyle !== bStyle) {
					lStyle = '';
				}
				return lStyle;
			}
		} else if (
			graphManager.getGraphEditor() &&
			graphManager.getGraphEditor().getGraphViewer() &&
			isShapeSelected()
		) {
			const f = selectionInfo.format;
			return f && f.linestyle;
		}

		return '';
	};

	const getFormatBorderWidth = () => {
		if (selectionInfo.sheetView) {
			const attr = selectionInfo.format;
			if (attr) {
				let lWidth = attr.leftborderwidth;
				const tWidth = attr.topborderwidth;
				const rWidth = attr.rightborderwidth;
				const bWidth = attr.bottomborderwidth;
				if (lWidth !== rWidth || lWidth !== tWidth || lWidth !== bWidth) {
					return '';
				}
				lWidth = Math.max(1, lWidth);
				if (lWidth !== 1.5) {
					// compensate old approach
					lWidth = Math.round(lWidth);
				}
				switch (lWidth) {
					case 1:
						return 1;
					case 1.5:
						return 25;
					case 2:
						return 50;
					case 3:
						return 75;
					case 4:
						return 100;
					case 8:
						return 200;
					case 12:
						return 300;
					case 16:
						return 400;
					default:
						break;
				}
			}
		} else if (
			graphManager.getGraphEditor() &&
			graphManager.getGraphEditor().getGraphViewer() &&
			isShapeSelected()
		) {
			return selectionInfo.format.linewidth;
		}

		return '';
	};

	const fillColorToRGBAObject = (fmt) => {
		let color = fmt && fmt.fillcolor ? fmt.fillcolor : '#FFFFFF';
		if (color.length && color[0] !== '#') {
			return color;
		}

		color = Strings.cut(color, '#');
		// cut off a leading #
		color = parseInt(color, 16);
		const r = color >> 16;
		const g = (color >> 8) & 0xff;
		const b = color & 0xff;
		const a = fmt && fmt.transparency ? fmt.transparency / 100 : 1;

		return { r, g, b, a };
	};

	const handleZoom = (event, value) => {
		if (typeof value === 'string') {
			value = value.replace(/%/, '');
			value = Number(value);
		}
		if (JSG.Numbers.isNumber(value)) {
			value = Math.min(Math.max(value, 25), 400);
			graphManager.getGraphEditor().setZoom(value / 100);
		} else {
			graphManager.getGraphEditor().setZoom(1);
		}
		updateState();
	};

	// isMachinePage() {
	// 	return window.location.href.indexOf('/machines/') > 0 || window.location.href.indexOf('/apps/') > 0;
	// }
	//
	const handleFocus = () => {
		graphManager.getCanvas().focus();
	};

	const getPresetColors = (color) => {
		const colors = [
			{ title: 'Black', color: '#000000' },
			{ title: 'Tundora', color: '#434343' },
			{ title: 'Dove Gray', color: '#666666' },
			{ title: 'Dusty Gray', color: '#999999' },
			{ title: 'Nobel', color: '#b7b7b7' },
			{ title: 'Silver', color: '#cccccc' },
			{ title: 'Alto', color: '#d9d9d9' },
			{ title: 'Gallery', color: '#efefef' },
			{ title: 'Concrete', color: '#f3f3f3' },
			{ title: 'White', color: '#ffffff' },
			{ title: 'Dark Red', color: '#980000' },
			{ title: 'Red', color: '#ff0000' },
			{ title: 'Orange', color: '#ff9900' },
			{ title: 'Yellow', color: '#ffff00' },
			{ title: 'Green', color: '#00ff00' },
			{ title: 'Light Blue', color: '#00ffff' },
			{ title: 'Cornflower Blue', color: '#4a86e8' },
			{ title: 'Blue', color: '#0000ff' },
			{ title: 'Electric Violet', color: '#9900ff' },
			{ title: 'Magenta ', color: '#ff00ff' },
			{ title: '#e6b8af', color: '#e6b8af' },
			{ title: '#f4cccc', color: '#f4cccc' },
			{ title: '#fce5cd', color: '#fce5cd' },
			{ title: '#fff2cc', color: '#fff2cc' },
			{ title: '#d9ead3', color: '#d9ead3' },
			{ title: '#d0e0e3', color: '#d0e0e3' },
			{ title: '#c9daf8', color: '#c9daf8' },
			{ title: '#cfe2f3', color: '#cfe2f3' },
			{ title: '#d9d2e9', color: '#d9d2e9' },
			{ title: '#ead1dc', color: '#ead1dc' },
			{ title: '#dd7e6b', color: '#dd7e6b' },
			{ title: '#ea9999', color: '#ea9999' },
			{ title: '#f9cb9c', color: '#f9cb9c' },
			{ title: '#ffe599', color: '#ffe599' },
			{ title: '#b6d7a8', color: '#b6d7a8' },
			{ title: '#a2c4c9', color: '#a2c4c9' },
			{ title: '#a4c2f4', color: '#a4c2f4' },
			{ title: '#9fc5e8', color: '#9fc5e8' },
			{ title: '#b4a7d6', color: '#b4a7d6' },
			{ title: '#d5a6bd', color: '#d5a6bd' },
			{ title: '#cc4125', color: '#cc4125' },
			{ title: '#e06666', color: '#e06666' },
			{ title: '#f6b26b', color: '#f6b26b' },
			{ title: '#ffd966', color: '#ffd966' },
			{ title: '#93c47d', color: '#93c47d' },
			{ title: '#76a5af', color: '#76a5af' },
			{ title: '#6d9eeb', color: '#6d9eeb' },
			{ title: '#6fa8dc', color: '#6fa8dc' },
			{ title: '#8e7cc3', color: '#8e7cc3' },
			{ title: '#c27ba0', color: '#c27ba0' },
			{ title: '#a61c00', color: '#a61c00' },
			{ title: '#cc0000', color: '#cc0000' },
			{ title: '#e69138', color: '#e69138' },
			{ title: '#f1c232', color: '#f1c232' },
			{ title: '#6aa84f', color: '#6aa84f' },
			{ title: '#45818e', color: '#45818e' },
			{ title: '#3c78d8', color: '#3c78d8' },
			{ title: '#3d85c6', color: '#3d85c6' },
			{ title: '#674ea7', color: '#674ea7' },
			{ title: '#a64d79', color: '#a64d79' },
			{ title: '#85200c', color: '#85200c' },
			{ title: '#990000', color: '#990000' },
			{ title: '#b45f06', color: '#b45f06' },
			{ title: '#bf9000', color: '#bf9000' },
			{ title: '#38761d', color: '#38761d' },
			{ title: '#134f5c', color: '#134f5c' },
			{ title: '#1155cc', color: '#1155cc' },
			{ title: '#0b5394', color: '#0b5394' },
			{ title: '#351c75', color: '#351c75' },
			{ title: '#741b47', color: '#741b47' },
			{ title: '#5b0f00', color: '#5b0f00' },
			{ title: '#660000', color: '#660000' },
			{ title: '#783f04', color: '#783f04' },
			{ title: '#7f6000', color: '#7f6000' },
			{ title: '#274e13', color: '#274e13' },
			{ title: '#0c343d', color: '#0c343d' },
			{ title: '#1c4587', color: '#1c4587' },
			{ title: '#073763', color: '#073763' },
			{ title: '#20124d', color: '#20124d' },
			{ title: '#4c1130', color: '#4c1130' }
		];

		colors.push({ title: 'None', color: 'transparent' });
		if (isChartElementSelected()) {
			colors.push({ title: 'Automatic', color: '#FFFFFE' });
		}

		if (color) {
			colors.forEach((colorl) => {
				if (colorl.color === color.toLowerCase()) {
					colorl.title += `${intl.formatMessage({ id: 'SheetToolbar.Current' }, {})}`;
				}
			});
		}

		return colors;
	};

	const { graph } = graphManager;
	if (!graph) {
		return null;
	}
	const { mediaSize } = props;
	const tf = selectionInfo.textFormat;
	const f = selectionInfo.format;
	const noChartFont = isChartElementWithoutFontSelected();
	const gridTitleColor = theme.components.MuiAppBar.styleOverrides.colorCaption.backgroundColor;
	const gridTitleFontColor = theme.components.MuiAppBar.styleOverrides.colorCaption.color;
	const gridTitleStyle = {
		backgroundColor: gridTitleColor,
		color: gridTitleFontColor,
		fontSize: '9pt',
		padding: '3px',
		height: '16px',
		display: 'flex',
		alignItems: 'center'
	};
	const selBorderStyle = getFormatBorderStyle();
	const selBorderWidth = getFormatBorderWidth();

	return (
		<AppBar
			id="sheet-toolbar"
			data-tour="sheet-toolbar"
			elevation={0}
			color="default"
			style={{
				height: '37px',
				minHeight: '37px',
				margin: 0,
				padding: '0px',
				position: 'relative',
				width: '100%',
				// borderTop: '1px solid lightgrey',
				border: '1px solid lightgrey',
				// borderTop: '1px solid #AAAAAA',
				overflow: 'hidden',
				display: 'flex',
				flexDirection: 'row'
			}}
		>
			<CustomTooltip key="ttInbox" header="Tooltip.Inbox" message="Tooltip.InboxMessage">
				<div>
					<IconButton
						data-tour="button-inboxsettings"
						disabled={!appEditable}
						onClick={() => props.showDialog('InboxSettings')}
						style={buttonStyle}
						size="large"
					>
						<SvgIcon>
							<path
								// eslint-disable-next-line max-len
								d="M18 5H6V7H18M6 9H18V11H6M2 12H4V17H20V12H22V17A2 2 0 0 1 20 19H4A2 2 0 0 1 2 17M18 13H6V15H18Z"
							/>
						</SvgIcon>
					</IconButton>
				</div>
			</CustomTooltip>
			<ToolSeparator key="ts1" left />
			<Autocomplete
				key="acZoom"
				sx={{
					marginTop: '4px',
					marginLeft: '4px',
					minWidth: '60px',
					width: '60px',
					fontSize: '0.85rem',
					'& .MuiAutocomplete-inputRoot': {
						border: 'none',
						paddingRight: '15px !important',
						'&:hover': {
							borderWidth: '1px'
						},
						'&:hover:not(.Mui-disabled):before': {
							borderWidth: '1px'
						},
						'&:before': {
							border: 'none'
						},
						'&:after': {
							borderWidth: '1px'
						}
					}
				}}
				disableClearable
				freeSolo
				forcePopupIcon
				clearIcon={null}
				blurOnSelect
				id="zoom"
				getOptionLabel={(option) => `${option}%`}
				value={Math.round(graphManager.getZoom() * 100)}
				onChange={(event, newValue) => handleZoom(event, newValue)}
				options={[25, 50, 75, 100, 125, 150, 200]}
				renderInput={(params) => <TextField variant="standard" {...params} />}
			/>
			<ToolSeparator key="ts2" left />
			<Tooltip key="ttUndo" enterDelay={300} title={<FormattedMessage id="Tooltip.Undo" defaultMessage="Undo" />}>
				<div>
					<IconButton
						style={buttonStyle}
						onClick={(e) => onUndo(e)}
						disabled={!canUndo || !appEditable}
						size="large"
					>
						<UndoIcon />
					</IconButton>
				</div>
			</Tooltip>
			<Tooltip key="ttRedo" enterDelay={300} title={<FormattedMessage id="Tooltip.Redo" defaultMessage="Redo" />}>
				<div>
					<IconButton
						style={buttonStyle}
						onClick={(e) => onRedo(e)}
						disabled={!canRedo || !appEditable}
						size="large"
					>
						<RedoIcon />
					</IconButton>
				</div>
			</Tooltip>
			<ToolSeparator key="ts3" left />
			{mediaSize !== 'large'
				? null
				: [
						<Tooltip
							key="ttThousand"
							enterDelay={300}
							title={
								<FormattedMessage
									id="Tooltip.FormatThousands"
									defaultMessage="Show Thousand Separators"
								/>
							}
						>
							<div>
								<IconButton
									onClick={() => onFormatNumber()}
									disabled={!isNumberFormatAvailable() || !appEditable}
									style={buttonStyle}
									size="large"
								>
									<SvgIcon>
										<text
											x="12"
											y="12"
											fontWeight="800"
											fontSize="9pt"
											dy="0.25em"
											textAnchor="middle"
										>
											000
										</text>
									</SvgIcon>
								</IconButton>
							</div>
						</Tooltip>,
						<Tooltip
							key="ttPercent"
							enterDelay={300}
							title={
								<FormattedMessage id="Tooltip.FormatPercent" defaultMessage="Numberformat Percent" />
							}
						>
							<div>
								<IconButton
									onClick={onFormatPercent}
									disabled={!isNumberFormatAvailable() || !appEditable}
									style={buttonStyle}
									size="small"
								>
									<SvgIcon>
										<text
											x="12"
											y="12"
											fontWeight="800"
											fontSize="12pt"
											dy="0.25em"
											textAnchor="middle"
										>
											%
										</text>
									</SvgIcon>
								</IconButton>
							</div>
						</Tooltip>,
						<Tooltip
							key="ttDecimals"
							enterDelay={300}
							title={
								<FormattedMessage id="Tooltip.FormatDecimalsLess" defaultMessage="Show less deoimals" />
							}
						>
							<div>
								<IconButton
									onClick={() => onFormatDecimals(false)}
									disabled={!isNumberFormatAvailable() || !appEditable}
									style={buttonStyle}
									size="large"
								>
									<SvgIcon>
										<path
											// eslint-disable-next-line max-len
											d="M12,17L15,20V18H21V16H15V14L12,17M9,5A3,3 0 0,1 12,8V11A3,3 0 0,1 9,14A3,3 0 0,1 6,11V8A3,3 0 0,1 9,5M9,7A1,1 0 0,0 8,8V11A1,1 0 0,0 9,12A1,1 0 0,0 10,11V8A1,1 0 0,0 9,7M4,12A1,1 0 0,1 5,13A1,1 0 0,1 4,14A1,1 0 0,1 3,13A1,1 0 0,1 4,12Z"
										/>
									</SvgIcon>
								</IconButton>
							</div>
						</Tooltip>,
						<Tooltip
							key="ttMDec"
							enterDelay={300}
							title={
								<FormattedMessage id="Tooltip.FormatDecimalsMore" defaultMessage="Show more deoimals" />
							}
						>
							<div>
								<IconButton
									onClick={() => onFormatDecimals(true)}
									disabled={!isNumberFormatAvailable() || !appEditable}
									style={buttonStyle}
									size="large"
								>
									<SvgIcon>
										<path
											// eslint-disable-next-line max-len
											d="M22,17L19,20V18H13V16H19V14L22,17M9,5A3,3 0 0,1 12,8V11A3,3 0 0,1 9,14A3,3 0 0,1 6,11V8A3,3 0 0,1 9,5M9,7A1,1 0 0,0 8,8V11A1,1 0 0,0 9,12A1,1 0 0,0 10,11V8A1,1 0 0,0 9,7M16,5A3,3 0 0,1 19,8V11A3,3 0 0,1 16,14A3,3 0 0,1 13,11V8A3,3 0 0,1 16,5M16,7A1,1 0 0,0 15,8V11A1,1 0 0,0 16,12A1,1 0 0,0 17,11V8A1,1 0 0,0 16,7M4,12A1,1 0 0,1 5,13A1,1 0 0,1 4,14A1,1 0 0,1 3,13A1,1 0 0,1 4,12Z"
										/>
									</SvgIcon>
								</IconButton>
							</div>
						</Tooltip>
				  ]}
			<Tooltip
				key="ttNumberFormat"
				enterDelay={300}
				title={<FormattedMessage id="Tooltip.FormatDecimalsSelect" defaultMessage="Choose Numberformat" />}
			>
				<div>
					<IconButton
						// sx={{ pt: '7px' }}
						onClick={(event) => onShowPopup(event, 'numberformat')}
						disabled={!isNumberFormatAvailable() || !appEditable}
					>
						<SvgIcon>
							<text
								x="12"
								y="12"
								// fontWeight="800"
								fontSize="0.85rem"
								dy="0.25em"
								textAnchor="middle"
							>
								123
							</text>
						</SvgIcon>
						<SvgIcon>
							<path d="M7,10L12,15L17,10H7Z" />
						</SvgIcon>
					</IconButton>
				</div>
			</Tooltip>
			{popupId !== 'numberformat' ? null : (
				<Popover
					key="popNumber"
					open={popupId === 'numberformat'}
					anchorEl={anchorEl}
					anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
					transformOrigin={{ horizontal: 'left', vertical: 'top' }}
					onClose={onClosePopup}
					TransitionProps={{
						onExited: handleFocus
					}}
				>
					<MenuList>
						{numberFormats.map((fmt) =>
							fmt.id[0] === '-' ? (
								<Divider key={fmt.id} />
							) : (
								<MenuItem dense key={fmt.id} onClick={() => onFormatNumberFormat(fmt)}>
									<div>
										<div
											style={{
												textAlign: 'left',
												display: 'inline-block',
												width: '125px'
											}}
										>
											{intl.formatMessage({ id: fmt.id }, {})}
										</div>
										<div
											style={{
												textAlign: 'right',
												display: 'inline-block',
												width: '125px'
											}}
										>
											{NumberFormatter.formatNumber(fmt.nf, fmt.value, fmt.type).formattedValue}
										</div>
									</div>
								</MenuItem>
							)
						)}
						<MenuItem
							dense
							key="nfcuston"
							onClick={() => {
								closePopup();
								props.showDialog('NumberFormatSettings');
							}}
						>
							<div>
								<div
									style={{
										textAlign: 'left',
										display: 'inline-block',
										width: '125px'
									}}
								>
									<FormattedMessage id="SheetToolbar.NumberFormats" defaultMessage="Other" />
								</div>
							</div>
						</MenuItem>
					</MenuList>
				</Popover>
			)}
			<ToolSeparator key="ts4" left={false} />
			{mediaSize !== 'small'
				? [
						<Select
							key="selectFontName"
							style={{
								width: '120px',
								fontSize: '0.85rem'
							}}
							id="font-name"
							value={tf.fontname || ''}
							onChange={(event) => onFormatFontName(event)}
							input={<Input name="font-name" id="font-name" />}
							sx={{
								'&:before': {
									// normal
									border: 'none !important'
								},
								'&:after': {
									// focused
									border: 'none'
								},
								'&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error):before': {
									// hover
									border: 'none'
								}
							}}
							// className={classes.select}
							disabled={noChartFont || !appEditable}
							inputProps={{
								style: { paddingLeft: '5px', paddingTop: '6px' }
							}}
						>
							<MenuItem dense value="Arial" key="s1">
								Arial
							</MenuItem>
							<MenuItem dense value="Courier New" key="s2">
								Courier New
							</MenuItem>
							<MenuItem dense value="Georgia" key="s3">
								Georgia
							</MenuItem>
							<MenuItem dense value="Lucida Console" key="s4">
								Lucida Console
							</MenuItem>
							<MenuItem dense value="Lucida Sans" key="s5">
								Lucida Sans
							</MenuItem>
							<MenuItem dense value="Palatino" key="s6">
								Palatino
							</MenuItem>
							<MenuItem dense value="Roboto" key="s9">
								Roboto
							</MenuItem>
							<MenuItem dense value="Tahoma" key="s7">
								Tahoma
							</MenuItem>
							<MenuItem dense value="Trebuchet MS" key="s8">
								Trebuchet MS
							</MenuItem>
							<MenuItem dense value="Verdana" key="s10">
								Verdana
							</MenuItem>
						</Select>,
						<Autocomplete
							key="acFontSize"
							disableClearable
							disabled={noChartFont || !appEditable}
							filterOptions={(x) => x}
							sx={{
								marginTop: '4px',
								marginLeft: '4px',
								minWidth: '35px',
								width: '35px',
								fontSize: '0.85rem',
								'& .MuiAutocomplete-input': {
									minWidth: '0px'
								},
								'& .MuiAutocomplete-inputRoot': {
									minWidth: '0px',
									width: '42px',
									'& .MuiAutocomplete-input': {
										minWidth: '0px'
									},
									border: 'none',
									paddingRight: '15px !important',
									'&:hover': {
										borderWidth: '1px'
									},
									'&:hover:not(.Mui-disabled):before': {
										borderWidth: '1px'
									},
									'&:before': {
										border: 'none'
									},
									'&:after': {
										borderWidth: '1px'
									}
								}
							}}
							freeSolo
							forcePopupIcon
							blurOnSelect
							id="fontsize"
							getOptionLabel={(option) => `${option}`}
							value={tf.fontsize || ''}
							onChange={(event, newValue) => onFormatFontSize(event, newValue)}
							options={[8, 9, 10, 12, 14, 18, 24, 30, 36, 48, 60, 72]}
							renderOption={(prop, option) => (
								<li {...prop} style={{ fontSize: '0.75rem', padding: '4px 8px', margin: '0px' }}>
									{option}
								</li>
							)}
							renderInput={(params) => <TextField variant="standard" {...params} />}
						/>,
						<Tooltip
							key="ttBold"
							enterDelay={300}
							title={<FormattedMessage id="Tooltip.FormatBold" defaultMessage="Bold" />}
						>
							<div>
								<IconButton
									style={{
										fontSize: '16pt',
										width: '34px',
										height: '27px',
										margin: '5px 0px 0px 10px',
										borderRadius: '0%',
										backgroundColor:
											tf.fontstyle & TextFormatAttributes.FontStyle.BOLD
												? JSG.theme.selectionback
												: 'transparent'
									}}
									onClick={onFormatBold}
									disabled={selectionInfo.type === '' || noChartFont || !appEditable}
									size="large"
								>
									<BoldIcon fontSize="inherit" />
								</IconButton>
							</div>
						</Tooltip>,
						<Tooltip
							key="ttItalic"
							enterDelay={300}
							title={<FormattedMessage id="Tooltip.FormatItalic" defaultMessage="Italic" />}
						>
							<div>
								<IconButton
									style={{
										width: '34px',
										height: '27px',
										margin: '5px 0px 0px 0px',
										fontSize: '16pt',
										borderRadius: '0%',
										backgroundColor:
											tf.fontstyle & TextFormatAttributes.FontStyle.ITALIC
												? JSG.theme.selectionback
												: 'transparent'
									}}
									onClick={onFormatItalic}
									disabled={selectionInfo.type === '' || noChartFont || !appEditable}
									size="large"
								>
									<ItalicIcon fontSize="inherit" />
								</IconButton>
							</div>
						</Tooltip>,
						<Tooltip
							key="ttFontColor"
							enterDelay={300}
							title={<FormattedMessage id="Tooltip.FormatFontColor" defaultMessage="Font Color" />}
						>
							<div>
								<IconButton
									style={buttonStyle}
									onClick={(event) => onShowPopup(event, 'fontcolor')}
									disabled={selectionInfo.type === '' || noChartFont || !appEditable}
									size="large"
								>
									<SvgIcon fontSize="inherit">
										<path fill={tf.fontcolor} d="M0 20h24v4H0z" />
										<path d="M11 3L5.5 17h2.25l1.12-3h6.25l1.12 3h2.25L13 3h-2zm-1.38 9L12 5.67 14.38 12H9.62z" />
									</SvgIcon>
								</IconButton>
							</div>
						</Tooltip>
				  ]
				: null}
			{mediaSize !== 'small' && popupId === 'fontcolor'
				? [
						<Popover
							key="popColor"
							open={popupId === 'fontcolor'}
							anchorEl={anchorEl}
							anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
							transformOrigin={{ horizontal: 'left', vertical: 'top' }}
							onClose={onClosePopup}
							TransitionProps={{
								onExited: handleFocus
							}}
						>
							<SketchPicker
								style={{
									boxShadow: 'none !important',
									backgroundColor: 'inherit'
								}}
								// classes={{ 'sketch-picker': classes.default }}
								width={250}
								disableAlpha
								presetColors={getPresetColors(tf.fontcolor)}
								color={tf.fontcolor}
								onChange={(color, event) => onFormatFontColor(color, event)}
							/>
						</Popover>
				  ]
				: null}
			{mediaSize !== 'small'
				? [
						<ToolSeparator key="ts5" left />,
						<CustomTooltip
							key="ttHAlign"
							header="Tooltip.FormatHAlignHeader"
							message="Tooltip.FormatHAlignMessage"
						>
							<div>
								<IconButton
									style={{
										height: '34px',
										width: '50px',
										padding: '0px',
										fontSize: '16pt'
									}}
									onClick={(event) => onShowPopup(event, 'halign')}
									disabled={selectionInfo.type === '' || isChartSelected() || !appEditable}
									size="large"
								>
									{tf.halign === 3 ? <FormatAlignJustify fontSize="inherit" /> : null}
									{tf.halign === 0 ? <FormatAlignLeft fontSize="inherit" /> : null}
									{tf.halign === 1 ? <FormatAlignCenter fontSize="inherit" /> : null}
									{tf.halign === 2 ? <FormatAlignRight fontSize="inherit" /> : null}
									<SvgIcon>
										<path
											// eslint-disable-next-line max-len
											d="M7,10L12,15L17,10H7Z"
										/>
									</SvgIcon>
								</IconButton>
							</div>
						</CustomTooltip>,
						<Popover
							key="popHAlign"
							open={popupId === 'halign'}
							anchorEl={anchorEl}
							anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
							transformOrigin={{ horizontal: 'left', vertical: 'top' }}
							onClose={onClosePopup}
							style={{
								overflow: 'hidden'
							}}
							TransitionProps={{
								onExited: handleFocus
							}}
						>
							<ToggleButtonGroup value={tf.halign} size="small" exclusive>
								<ToggleButton value="3" key="justify" onClick={() => onFormatHorizontalAlign(3)}>
									<FormatAlignJustify />
								</ToggleButton>
								<ToggleButton value="0" key="left" onClick={() => onFormatHorizontalAlign(0)}>
									<FormatAlignLeft />
								</ToggleButton>
								<ToggleButton value="1" key="center" onClick={() => onFormatHorizontalAlign(1)}>
									<FormatAlignCenter />
								</ToggleButton>
								<ToggleButton value="2" key="right" onClick={() => onFormatHorizontalAlign(2)}>
									<FormatAlignRight />
								</ToggleButton>
							</ToggleButtonGroup>
						</Popover>,
						<CustomTooltip
							key="ttVAlign"
							header="Tooltip.FormatVAlignHeader"
							message="Tooltip.FormatVAlignMessage"
						>
							<div>
								<IconButton
									style={{
										height: '34px',
										width: '50px',
										padding: '0px',
										fontSize: '16pt'
									}}
									onClick={(event) => onShowPopup(event, 'valign')}
									disabled={selectionInfo.type !== 'cell' || !appEditable}
									size="large"
								>
									{tf.valign === 0 ? <VerticalAlignTop fontSize="inherit" /> : null}
									{tf.valign === 1 ? <VerticalAlignCenter fontSize="inherit" /> : null}
									{tf.valign === 2 ? <VerticalAlignBottom fontSize="inherit" /> : null}
									<SvgIcon>
										<path
											// eslint-disable-next-line max-len
											d="M7,10L12,15L17,10H7Z"
										/>
									</SvgIcon>
								</IconButton>
							</div>
						</CustomTooltip>,
						<Popover
							key="popVAlign"
							open={popupId === 'valign'}
							anchorEl={anchorEl}
							anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
							transformOrigin={{ horizontal: 'left', vertical: 'top' }}
							onClose={onClosePopup}
							style={{
								overflow: 'hidden'
							}}
							TransitionProps={{
								onExited: handleFocus
							}}
						>
							<ToggleButtonGroup value={tf.valign} size="small" exclusive>
								<ToggleButton value="0" key="top" onClick={() => onFormatVerticalAlign(0)}>
									<VerticalAlignTop />
								</ToggleButton>
								<ToggleButton value="1" key="middle" onClick={() => onFormatVerticalAlign(1)}>
									<VerticalAlignCenter />
								</ToggleButton>
								<ToggleButton value="2" key="bottom" onClick={() => onFormatVerticalAlign(2)}>
									<VerticalAlignBottom />
								</ToggleButton>
							</ToggleButtonGroup>
						</Popover>,
						<ToolSeparator key="ts6" />
				  ]
				: null}
			<Tooltip
				key="ttFillColor"
				enterDelay={300}
				title={<FormattedMessage id="Tooltip.FormatFillColor" defaultMessage="Fill Color" />}
			>
				<div>
					<IconButton
						style={buttonStyle}
						onClick={(event) => onShowPopup(event, 'fillcolor')}
						disabled={selectionInfo.type === '' || !appEditable}
						size="large"
					>
						<SvgIcon fontSize="inherit">
							<path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15c-.59.59-.59 1.54 0 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z" />
							<rect
								stroke={f && f.fillcolor && f.fillstyle !== 0 ? 'none' : JSG.theme.border}
								fill={f && f.fillcolor && f.fillstyle !== 0 ? f.fillcolor : 'none'}
								// d="M0 20h24v4H0z"
								y="20" x="0" width="20" height="4"
							/>
						</SvgIcon>
					</IconButton>
				</div>
			</Tooltip>
			{popupId !== 'fillcolor' ? null : (
				<Popover
					key="popFillColor"
					open={popupId === 'fillcolor'}
					anchorEl={anchorEl}
					anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
					transformOrigin={{ horizontal: 'left', vertical: 'top' }}
					onClose={onClosePopup}
					TransitionProps={{
						onExited: handleFocus
					}}
				>
					<SketchPicker
						disableAlpha={!isChartSelected()}
						width={250}
						color={fillColorToRGBAObject(f)}
						presetColors={getPresetColors(f && f.fillcolor ? f.fillcolor : '')}
						onChange={(color, event) => onFormatFillColor(color, event)}
					/>
				</Popover>
			)}
			<Tooltip
				key="ttLineColor"
				enterDelay={300}
				title={<FormattedMessage id="Tooltip.FormatLine" defaultMessage="Line Format" />}
			>
				<div>
					<IconButton
						style={buttonStyle}
						onClick={(event) => onShowPopup(event, 'bordercolor')}
						disabled={selectionInfo.type === '' || !appEditable}
						size="large"
					>
						<SvgIcon fontSize="inherit">
							<path d="M17.75 7L14 3.25l-10 10V17h3.75l10-10zm2.96-2.96c.39-.39.39-1.02 0-1.41L18.37.29a.9959.9959 0 0 0-1.41 0L15 2.25 18.75 6l1.96-1.96z" />
							<path fill={getFormatBorderColor()} d="M0 20h24v4H0z" />
						</SvgIcon>
					</IconButton>
				</div>
			</Tooltip>
			{popupId !== 'bordercolor' ? null : (
				<Popover
					key="popBorderColor"
					open={popupId === 'bordercolor'}
					anchorEl={anchorEl}
					anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
					transformOrigin={{ horizontal: 'left', vertical: 'top' }}
					onClose={onClosePopup}
					TransitionProps={{
						onExited: handleFocus
					}}
				>
					<SketchPicker
						disableAlpha
						width={250}
						color={getFormatBorderColor()}
						onChange={(color, event) => onFormatBorderColor(color, event)}
						presetColors={getPresetColors(getFormatBorderColor())}
					/>
				</Popover>
			)}
			<Tooltip
				key="ttBorders"
				enterDelay={300}
				title={<FormattedMessage id="Tooltip.FormatBorder" defaultMessage="Border Format" />}
			>
				<div>
					<IconButton
						style={buttonStyle}
						onClick={(event) => onShowPopup(event, 'borders')}
						disabled={selectionInfo.type !== 'cell' || !appEditable}
						size="large"
					>
						<BorderAllIcon fontSize="inherit" />
					</IconButton>
				</div>
			</Tooltip>
			{popupId !== 'borders' ? null : (
				<Popover
					key="popBorders"
					open={popupId === 'borders'}
					anchorEl={anchorEl}
					anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
					transformOrigin={{ horizontal: 'left', vertical: 'top' }}
					onClose={onClosePopup}
					style={{
						overflow: 'hidden'
					}}
					TransitionProps={{
						onExited: handleFocus
					}}
				>
					<ImageList
						cols={6}
						rowHeight={32}
						gap={2}
						style={{
							width: '240px',
							margin: '4px'
						}}
					>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '4px' }} onClick={() => onFormatBorder('all')} size="large">
								<BorderAllIcon fontSize="inherit" />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '4px' }} onClick={() => onFormatBorder('inner')} size="large">
								<BorderInnerIcon fontSize="inherit" />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '4px' }}
								onClick={() => onFormatBorder('horizontal')}
								size="large"
							>
								<BorderHorizontalIcon fontSize="inherit" />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '4px' }}
								onClick={() => onFormatBorder('vertical')}
								size="large"
							>
								<BorderVerticalIcon fontSize="inherit" />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '4px' }} onClick={() => onFormatBorder('outer')} size="large">
								<BorderOuterIcon fontSize="inherit" />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '4px' }} onClick={() => onFormatBorder('left')} size="large">
								<BorderLeftIcon fontSize="inherit" />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '4px' }} onClick={() => onFormatBorder('top')} size="large">
								<BorderTopIcon fontSize="inherit" />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '4px' }} onClick={() => onFormatBorder('right')} size="large">
								<BorderRightIcon fontSize="inherit" />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '4px' }}
								onClick={() => onFormatBorder('bottom')}
								size="large"
							>
								<BorderBottomIcon fontSize="inherit" />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '4px' }} onClick={() => onFormatBorder('clear')} size="large">
								<BorderClearIcon fontSize="inherit" />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '4px' }}
								onClick={() => onFormatBorder('outerfat')}
								size="large"
							>
								<SvgIcon>
									<path d="M 9 11 L 7 11 L 7 13 L 9 13 M 13 15 L 11 15 L 11 17 L 13 17 M 19 19 L 5 19 L 5 5 L 19 5 M 1.77 22.23 L 22.246 22.322 L 22.276 1.462 L 1.662 1.432 M 17 11 L 15 11 L 15 13 L 17 13 M 13 11 L 11 11 L 11 13 L 13 13 M 13 7 L 11 7 L 11 9 L 13 9 L 13 7 Z" />
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '4px' }}
								onClick={() => onFormatBorder('bottomfat')}
								size="large"
							>
								<SvgIcon>
									<path d="M 5 15 L 3 15 L 3 17 L 5 17 M 3.027 22.305 L 20.973 22.414 L 21 19 L 3.041 19 M 5 11 L 3 11 L 3 13 L 5 13 M 19 9 L 21 9 L 21 7 L 19 7 M 19 5 L 21 5 L 21 3 L 19 3 M 5 7 L 3 7 L 3 9 L 5 9 M 19 17 L 21 17 L 21 15 L 19 15 M 19 13 L 21 13 L 21 11 L 19 11 M 17 3 L 15 3 L 15 5 L 17 5 M 13 3 L 11 3 L 11 5 L 13 5 M 17 11 L 15 11 L 15 13 L 17 13 M 13 7 L 11 7 L 11 9 L 13 9 M 5 3 L 3 3 L 3 5 L 5 5 M 13 11 L 11 11 L 11 13 L 13 13 M 9 3 L 7 3 L 7 5 L 9 5 M 13 15 L 11 15 L 11 17 L 13 17 M 9 11 L 7 11 L 7 13 L 9 13 L 9 11 Z" />
								</SvgIcon>
							</IconButton>
						</ImageListItem>
					</ImageList>
				</Popover>
			)}
			<Tooltip
				key="ttBorderStyle"
				enterDelay={300}
				title={<FormattedMessage id="Tooltip.BorderStyle" defaultMessage="Border Style" />}
			>
				<div>
					<IconButton
						style={buttonStyle}
						onClick={(event) => onShowPopup(event, 'borderstyle')}
						disabled={selectionInfo.type === '' || !appEditable}
						size="large"
					>
						<SvgIcon>
							<path d="M3,16H8V14H3V16M9.5,16H14.5V14H9.5V16M16,16H21V14H16V16M3,20H5V18H3V20M7,20H9V18H7V20M11,20H13V18H11V20M15,20H17V18H15V20M19,20H21V18H19V20M3,12H11V10H3V12M13,12H21V10H13V12M3,4V8H21V4H3Z" />
						</SvgIcon>
					</IconButton>
				</div>
			</Tooltip>
			{popupId !== 'borderstyle' ? null : (
				<Popover
					key="popBorderStyle"
					open={popupId === 'borderstyle'}
					anchorEl={anchorEl}
					anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
					transformOrigin={{ horizontal: 'left', vertical: 'top' }}
					onClose={onClosePopup}
					style={{
						overflow: 'hidden'
					}}
					TransitionProps={{
						onExited: handleFocus
					}}
				>
					<ImageList
						cols={1}
						rowHeight={24}
						gap={4}
						style={{
							width: '100px',
							margin: '1px'
						}}
					>
						<ImageListItem
							cols={1}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="SheetToolbar.BorderStyle" defaultMessage="Border Style" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={borderStyle}
								onClick={() => onFormatBorderStyle(FormatAttributes.LineStyle.NONE)}
								size="large"
							>
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderStyle === FormatAttributes.LineStyle.NONE ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H99 V24 H-99 V-24" />
									) : null}
									<text
										x="50"
										y="14"
										fontWeight="normal"
										fontSize="9pt"
										dy="0.25em"
										textAnchor="middle"
									>
										{intl.formatMessage({ id: 'None' }, {})}
									</text>
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={borderStyle}
								onClick={() => onFormatBorderStyle(FormatAttributes.LineStyle.SOLID)}
								size="large"
							>
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									stroke="currentColor"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderStyle === FormatAttributes.LineStyle.SOLID ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H99 V24 H-99 V-24" />
									) : null}
									<path d="M5,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={borderStyle}
								onClick={() => onFormatBorderStyle(FormatAttributes.LineStyle.DOT)}
								size="large"
							>
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									stroke="currentColor"
									strokeDasharray="1,2"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderStyle === FormatAttributes.LineStyle.DOT ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H98 V24 H-98 V-24" />
									) : null}
									<path d="M5,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={borderStyle}
								onClick={() => onFormatBorderStyle(FormatAttributes.LineStyle.DASH)}
								size="large"
							>
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									stroke="currentColor"
									strokeDasharray="5,5"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderStyle === FormatAttributes.LineStyle.DASH ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H98 V24 H-98 V-24" />
									) : null}
									<path d="M5,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={borderStyle}
								onClick={() => onFormatBorderStyle(FormatAttributes.LineStyle.DASHDOT)}
								size="large"
							>
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									stroke="currentColor"
									strokeDasharray="5,5,1,5"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderStyle === FormatAttributes.LineStyle.DASHDOT ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H98 V24 H-98 V-24" />
									) : null}
									<path d="M5,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={borderStyle}
								onClick={() => onFormatBorderStyle(FormatAttributes.LineStyle.DASHDOTDOT)}
								size="large"
							>
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									stroke="currentColor"
									strokeDasharray="5,5,1,2,1,5"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderStyle === FormatAttributes.LineStyle.DASHDOTDOT ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H98 V24 H-98 V-24" />
									) : null}
									<path d="M5,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem
							cols={1}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="SheetToolbar.BorderWidth" defaultMessage="Border Width" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={borderStyle} onClick={() => onFormatBorderWidth(-1)} size="large">
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderWidth === -1 || selBorderWidth === 1 ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H99 V24 H-99 V-24" />
									) : null}
									<text x="35" y="14" fontWeight="normal" fontSize="8pt" dy="0.25em" textAnchor="end">
										1 px
									</text>
									<path stroke="currentColor" d="M42,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={borderStyle} onClick={() => onFormatBorderWidth(25)} size="large">
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderWidth === 25 ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H99 V24 H-99 V-24" />
									) : null}
									<text x="35" y="14" fontWeight="normal" fontSize="8pt" dy="0.25em" textAnchor="end">
										1/4 mm
									</text>
									<path stroke="currentColor" strokeWidth="0.25mm" d="M42,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={borderStyle} onClick={() => onFormatBorderWidth(50)} size="large">
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderWidth === 50 ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H99 V24 H-99 V-24" />
									) : null}
									<text x="35" y="14" fontWeight="normal" fontSize="8pt" dy="0.25em" textAnchor="end">
										1/2 mm
									</text>
									<path stroke="currentColor" strokeWidth="0.5mm" d="M42,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={borderStyle} onClick={() => onFormatBorderWidth(75)} size="large">
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderWidth === 75 ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H99 V24 H-99 V-24" />
									) : null}
									<text x="35" y="14" fontWeight="normal" fontSize="8pt" dy="0.25em" textAnchor="end">
										3/4 mm
									</text>
									<path stroke="currentColor" strokeWidth="0.75mm" d="M42,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={borderStyle} onClick={() => onFormatBorderWidth(100)} size="large">
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderWidth === 100 ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H99 V24 H-99 V-24" />
									) : null}
									<text x="35" y="14" fontWeight="normal" fontSize="8pt" dy="0.25em" textAnchor="end">
										1 mm
									</text>
									<path stroke="currentColor" strokeWidth="1mm" d="M42,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={borderStyle} onClick={() => onFormatBorderWidth(200)} size="large">
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderWidth === 200 ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H99 V24 H-99 V-24" />
									) : null}
									<text x="35" y="14" fontWeight="normal" fontSize="8pt" dy="0.25em" textAnchor="end">
										2 mm
									</text>
									<path stroke="currentColor" strokeWidth="2mm" d="M42,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={borderStyle} onClick={() => onFormatBorderWidth(300)} size="large">
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderWidth === 300 ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H99 V24 H-99 V-24" />
									) : null}
									<text x="35" y="14" fontWeight="normal" fontSize="8pt" dy="0.25em" textAnchor="end">
										3 mm
									</text>
									<path stroke="currentColor" strokeWidth="3mm" d="M42,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={borderStyle} onClick={() => onFormatBorderWidth(400)} size="large">
								<svg
									width="100"
									height="28"
									viewBox="0 0 100 28"
									fill="currentColor"
									xmlns="http://www.w3.org/2000/svg"
								>
									{selBorderWidth === 400 ? (
										<path stroke="none" fill={JSG.theme.selectionback} d="M1,1 H99 V24 H-99 V-24" />
									) : null}
									<text x="35" y="14" fontWeight="normal" fontSize="8pt" dy="0.25em" textAnchor="end">
										4 mm
									</text>
									<path stroke="currentColor" strokeWidth="4mm" d="M42,14 L95,14" />
								</svg>
							</IconButton>
						</ImageListItem>
					</ImageList>
				</Popover>
			)}
			{mediaSize !== 'large'
				? null
				: [
						<ToolSeparator key="ts7" left />,
						<Tooltip
							key="ttRef"
							enterDelay={300}
							title={
								<FormattedMessage
									id="Tooltip.ShowReferences"
									defaultMessage="Visualize Cell Dependencies"
								/>
							}
						>
							<div>
								<IconButton
									style={buttonStyle}
									onClick={(event) => onShowPopup(event, 'dependencies')}
									disabled={selectionInfo.type !== 'cell' || !appEditable}
									size="large"
								>
									<SvgIcon>
										<path
											// eslint-disable-next-line max-len
											d="M12 2L16 6H13V13.85L19.53 17.61L21 15.03L22.5 20.5L17 21.96L18.53 19.35L12 15.58L5.47 19.35L7 21.96L1.5 20.5L3 15.03L4.47 17.61L11 13.85V6H8L12 2M21 5H19V3H21V5M22 10V12H18V10H19V8H18V6H21V10H22Z"
										/>
									</SvgIcon>
								</IconButton>
							</div>
						</Tooltip>,
						<Popover
							key="popDep"
							open={popupId === 'dependencies'}
							anchorEl={anchorEl}
							anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
							transformOrigin={{ horizontal: 'left', vertical: 'top' }}
							onClose={onClosePopup}
							TransitionProps={{
								onExited: handleFocus
							}}
						>
							<MenuList>
								<MenuItem dense onClick={() => onShowPredecessors()}>
									<ListItemText
										primary={
											<FormattedMessage
												id="SheetToolbar.ShowPredecessors"
												defaultMessage="Show Predecessors"
											/>
										}
									/>
								</MenuItem>
								<MenuItem dense onClick={() => onShowDependants()}>
									<ListItemText
										primary={
											<FormattedMessage
												id="SheetToolbar.ShowDependants"
												defaultMessage="Show depending Cells"
											/>
										}
									/>
								</MenuItem>
								<Divider />
								<MenuItem dense onClick={() => onRemoveArrows()}>
									<ListItemText
										primary={
											<FormattedMessage
												id="SheetToolbar.RemoveCellInfo"
												defaultMessage="Remove Arrows"
											/>
										}
									/>
								</MenuItem>
							</MenuList>
						</Popover>
				  ]}
			<ToolSeparator key="ts8" left />
			<Tooltip
				key="ttTool"
				enterDelay={300}
				title={<FormattedMessage id="Tooltip.InsertTool" defaultMessage="Insert Tool" />}
			>
				<div>
					<IconButton
						style={buttonStyle}
						disabled={!appEditable}
						onClick={(event) => onShowPopup(event, 'tools')}
						size="large"
					>
						<SvgIcon stroke="currentColor" strokeWidth="2px">
							<rect fillOpacity="0.7" fill="currentColor" x="2" y="3" width="12" height="12" />
							<circle fill="white" cx="14" cy="15" r={7} />
						</SvgIcon>
					</IconButton>
				</div>
			</Tooltip>
			{popupId !== 'tools' ? null : (
				<Popover
					key="popTools"
					open={popupId === 'tools'}
					anchorEl={anchorEl}
					anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
					transformOrigin={{ horizontal: 'right', vertical: 'top' }}
					transitionDuration={{ exit: 0 }}
					onClose={onClosePopup}
					style={{
						overflow: 'hidden'
					}}
					TransitionProps={{
						onExited: handleFocus
					}}
				>
					<ImageList
						cols={6}
						rowHeight={35}
						gap={2}
						style={{
							width: '240px',
							margin: '2px'
						}}
					>
						<ImageListItem
							cols={6}
							style={{
								height: '23px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="SheetToolbar.ToolsStandard" defaultMessage="Standard" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={onCreateLine} size="large">
								<SvgIcon>
									<path d="M3.5 22 L22 3.5 L20.5 2 L2 20.5 Z" />
								</SvgIcon>
								{/* <ToolLineIcon /> */}
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={onCreateArrow} size="large">
								<ToolArrowIcon />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={onCreateRectangle} size="large">
								<ToolRectangleIcon />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={onCreateEllipse} size="large">
								<ToolEllipseIcon />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={onCreateText} size="large">
								<FormatAlignCenter />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={onCreatePolyline} size="large">
								<ToolPolylineIcon />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={() => onCreateTool('arc')} size="large">
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 32">
									<path
										fill="none"
										strokeWidth="2.5px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M24,12 C24,5.42 18.58,0 12,0 C5.42,0 0,5.42 0,12 C0,18.58 5.42,24 12,24"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('arcclosed')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 32">
									<path
										fill="none"
										strokeWidth="2.5px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M24,12 C24,5.42 18.58,0 12,0 C5.42,0 0,5.42 0,12 C0,18.58 5.42,24 12,24 C12,24 24,12 24,12 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={() => onCreateTool('pie')} size="large">
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 32">
									<path
										fill="none"
										strokeWidth="2.5px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M24,12 C24,5.42 18.58,0 12,0 C5.42,0 0,5.42 0,12 C0,18.58 5.42,24 12,24 C12,24 12,12 12,12 C12,12 24,12 24,12 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem
							cols={6}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="SheetToolbar.ToolsRectangles" defaultMessage="Rectangles" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('roundRect')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M6.6,0 C8.8,0 13.2,0 21.4,0 C25.03,0 28,2.97 28,6.6 C28,8.8 28,13.2 28,15.4 C28,19.03 25.03,22 21.4,22 C13.2,22 8.8,22 6.6,22 C2.97,22 0,19.03 0,15.4 C0,13.2 0,8.8 0,6.6 C0,2.97 2.97,0 6.6,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('roundRectCornerCut')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M0,0 C4.4,0 13.2,0 17.01,0 C23.06,0 28.01,4.95 28.01,11 C28.01,8.8 28.01,17.6 28.01,22 C17.6,22 4.4,22 0,22 C0,17.6 0,4.4 0,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('roundRectCornerCutSame')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M11,0 C8.8,0 13.2,0 17,0 C23.05,0 28,4.95 28,11 C28,8.8 28,13.2 28,22 C28,22 28,22 28,22 C13.2,22 8.8,22 0,22 C0,22 0,22 0,22 C0,13.2 0,8.8 0,11 C0,4.95 4.95,0 11,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('roundRectCornerCutDiagonal')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M0,0 C8.8,0 13.2,0 17.01,0 C23.06,0 28.01,4.95 28.01,11 C28.01,8.8 28.01,13.2 28.01,22 C28.01,22 28.01,22 28.01,22 C13.2,22 8.8,22 11,22 C4.95,22 0,17.05 0,11 C0,13.2 0,8.8 0,0 C0,0 0,0 0,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('rectCornerCut')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M0,0 L0,22 L28.01,22 L28.01,8.14 L19.87,0 L0,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('rectCornerCutSame')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M8.14,0 L0,8.14 L0,22 L0,22 L28.01,22 L28.01,22 L28.01,8.14 L19.87,0 L8.14,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('rectCornerCutDiagonal')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M8.2,0 L0,8.2 L0,22 L0,22 L19.8,22 L28,13.8 L28,0 L28,0 L8.2,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('trapezoidalTop')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M4.4,0 L23.6,0 L28,22 L0,22 L4.4,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('trapezoidalBottom')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M0,0 L28.01,0 L23.61,22 L4.4,22 L0,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('parallelogrammTopToRight')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M5.6,0 L28.01,0 L22.41,22 L0,22 L5.6,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('parallelogrammLeftToBottom')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M0,4.4 L28.01,0 L28.01,17.6 L0,22 L0,4.4 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem
							cols={6}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="SheetToolbar.ToolsStars" defaultMessage="Stars" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={() => onCreateTool('star3')} size="large">
								<SvgIcon>
									<path
										// eslint-disable-next-line max-len
										d="M12,9.5L13.2,13.5L16,16.5L12,15.6L7.9,16.5L10.7,13.5L12,9.5M12,2.6L9,12.4L2,19.9L12,17.6L22,20L15,12.5L12,2.6Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={() => onCreateTool('star4')} size="large">
								<SvgIcon>
									<path
										// eslint-disable-next-line max-len
										d="M12,6.7L13.45,10.55L17.3,12L13.45,13.45L12,17.3L10.55,13.45L6.7,12L10.55,10.55L12,6.7M12,1L9,9L1,12L9,15L12,23L15,15L23,12L15,9L12,1Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={() => onCreateTool('star5')} size="large">
								<ToolStar />
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={() => onCreateTool('star6')} size="large">
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 32">
									<path
										fill="none"
										strokeWidth="2px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M12,0 L14.4,7.82 L22.44,6 L16.8,12 L22.44,18 L14.4,16.18 L12,24 L9.6,16.18 L1.56,18 L7.2,12 L1.56,6 L9.6,7.82 L12,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={() => onCreateTool('star8')} size="large">
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 32">
									<path
										fill="none"
										strokeWidth="2px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M12,0 L13.82,7.58 L20.52,3.48 L16.42,10.18 L24,12 L16.42,13.82 L20.52,20.52 L13.82,16.42 L12,24 L10.18,16.42 L3.48,20.52 L7.58,13.82 L0,12 L7.58,10.18 L3.48,3.48 L10.18,7.58 L12,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem
							cols={6}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="SheetToolbar.ToolsArrows" defaultMessage="Arrows" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('arrowLeft')}
								size="large"
							>
								<SvgIcon>
									<path d="M13,22L3,12L13,2V8H21V16H13V22M6,12L11,17V14H19V10H11V7L6,12Z" />
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('arrowRight')}
								size="large"
							>
								<SvgIcon>
									<path d="M11,16H3V8H11V2L21,12L11,22V16M13,7V10H5V14H13V17L18,12L13,7Z" />
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={() => onCreateTool('arrowUp')} size="large">
								<SvgIcon>
									<path d="M16,13V21H8V13H2L12,3L22,13H16M7,11H10V19H14V11H17L12,6L7,11Z" />
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('arrowDown')}
								size="large"
							>
								<SvgIcon>
									<path d="M22,11L12,21L2,11H8V3H16V11H22M12,18L17,13H14V5H10V13H7L12,18Z" />
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('arrowDblHorz')}
								size="large"
							>
								<SvgIcon>
									<path
										// eslint-disable-next-line max-len
										d="M14,16V22L24,12L14,2V8H10V2L0,12L10,22V16H14M8,14V17L3,12L8,7V10H16V7L21,12L16,17V14H8Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('arrowDblVert')}
								size="large"
							>
								<SvgIcon>
									<path
										// eslint-disable-next-line max-len
										d="M16,10H22L12,0L2,10H8V14H2L12,24L22,14H16V10M14,16H17L12,21L7,16H10V8H7L12,3L17,8H14V16Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('arrowTopLeft')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 32">
									<path
										fill="none"
										strokeWidth="2px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										d="M16.8,4.8 L14.4,4.8 L19.2,0 L24,4.8 L21.6,4.8 L21.6,21.6 L4.8,21.6 L4.8,24 L0,19.2 L4.8,14.4 L4.8,16.8 L16.8,16.8 L16.8,4.8 Z"
										// eslint-disable-next-line max-len
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('arrowTopRight')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 32">
									<path
										fill="none"
										strokeWidth="2px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M7.2,4.8 L9.6,4.8 L4.8,0 L0,4.8 L2.4,4.8 L2.4,21.6 L19.2,21.6 L19.2,24 L24,19.2 L19.2,14.4 L19.2,16.8 L7.2,16.8 L7.2,4.8 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem
							cols={6}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="SheetToolbar.ToolsLegends" defaultMessage="Legends" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton style={{ padding: '5px' }} onClick={() => onCreateTool('callout')} size="large">
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M0,0 L28,0 L28,22 L9.8,22 L2.8,30.8 L4.2,22 L0,22 L0,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('calloutline')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M0,0 L28,0 L28,22 L7,22 L-0.28,34.32 L7,22 L0,22 L0,0 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('calloutroundrect')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M0,4.4 C0,1.98 1.98,0 4.4,0 C4.4,0 23.6,0 23.6,0 C26.02,0 28,1.98 28,4.4 C28,4.4 28,17.6 28,17.6 C28,20.02 26.02,22 23.6,22 C23.6,22 11.2,22 11.2,22 C11.2,22 2.8,30.8 2.8,30.8 C2.8,30.8 5.6,22 5.6,22 C5.6,22 4.4,22 4.4,22 C1.98,22 0,20.02 0,17.6 C0,17.6 0,4.4 0,4.4 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('calloutroundrectline')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M0,4.4 C0,1.98 1.98,0 4.4,0 C4.4,0 23.6,0 23.6,0 C26.02,0 28,1.98 28,4.4 C28,4.4 28,17.6 28,17.6 C28,20.02 26.02,22 23.6,22 C23.6,22 8.4,22 8.4,22 C8.4,22 2.8,30.8 2.8,30.8 C2.8,30.8 8.4,22 8.4,22 C8.4,22 4.4,22 4.4,22 C1.98,22 0,20.02 0,17.6 C0,17.6 0,4.4 0,4.4 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('calloutround')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M11.08,21.76 C18.08,22.93 25.15,19.71 27.33,14.36 C29.52,9.01 26.14,3.15 19.58,0.91 C13.02,-1.33 5.28,0.72 1.78,5.63 C-1.71,10.53 0.11,16.79 5.98,20.01 C5.98,20.01 3.36,30.36 3.36,30.36 C3.36,30.36 11.08,21.76 11.08,21.76 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem cols={1}>
							<IconButton
								style={{ padding: '5px' }}
								onClick={() => onCreateTool('calloutroundline')}
								size="large"
							>
								<SvgIcon style={{ stroke: 'currentColor' }} viewBox="-5 -5 32 38">
									<path
										fill="none"
										strokeWidth="3px"
										strokeLinejoin="miter"
										strokeMiterlimit="5"
										// eslint-disable-next-line max-len
										d="M8.31,21.05 C15.33,23.5 23.67,20.98 26.79,15.47 C29.91,9.95 26.7,3.4 19.69,0.95 C12.67,-1.5 4.33,1.02 1.21,6.53 C-1.91,12.05 1.29,18.6 8.31,21.05 C8.31,21.05 2.8,30.8 2.8,30.8 C2.8,30.8 8.31,21.05 8.31,21.05 Z"
									/>
								</SvgIcon>
							</IconButton>
						</ImageListItem>
						<ImageListItem
							cols={6}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="SheetToolbar.ToolsControls" defaultMessage="Controls" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								key="ttInsertButton"
								enterDelay={300}
								title={
									<FormattedMessage id="Tooltip.InsertButton" defaultMessage="Create Push Button" />
								}
							>
								<IconButton
									style={{ padding: '5px' }}
									onClick={() => onCreateControl('button')}
									size="large"
								>
									<SvgIcon>
										<rect stroke="none" x="2" y="5" width="22" height="14" />
										<text
											fill="white"
											textAnchor="middle"
											fontSize="6pt"
											dominantBaseline="central"
											x="12"
											y="12"
										>
											OK
										</text>
									</SvgIcon>
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								key="ttInsertCheck"
								enterDelay={300}
								title={
									<FormattedMessage
										id="Tooltip.InsertCheckbox"
										defaultMessage="Create Checkbox Control"
									/>
								}
							>
								<IconButton
									style={{ padding: '5px' }}
									onClick={() => onCreateControl('checkbox')}
									size="large"
								>
									<SvgIcon>
										<rect
											fill="none"
											strokeWidth="3"
											stroke="#888888"
											x="4"
											y="4"
											width="16"
											height="16"
										/>
										<path fill="none" strokeWidth="2" stroke="#888888" d="M7 12 L12 16 L17 7" />
									</SvgIcon>
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								key="ttInsertSlider"
								enterDelay={300}
								title={
									<FormattedMessage
										id="Tooltip.InsertSlider"
										defaultMessage="Create Slider Control"
									/>
								}
							>
								<IconButton
									style={{ padding: '5px' }}
									onClick={() => onCreateControl('slider')}
									size="large"
								>
									<SvgIcon>
										<path fill="none" strokeWidth="1.5" stroke="#888888" d="M2 10 h20 v4 h-20 z" />
										<path fill="none" stroke="#888888" d="M7 10 v4" />
										<path fill="none" stroke="#888888" d="M12 10 v4" />
										<path fill="none" stroke="#888888" d="M17 10 v4" />
										<path fill="888888" stroke="#888888" d="M10 6 L8 9 L6 6z" />
									</SvgIcon>
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								key="ttInsertKnob"
								enterDelay={300}
								title={
									<FormattedMessage id="Tooltip.InsertKnob" defaultMessage="Create Knob Control" />
								}
							>
								<IconButton
									style={{ padding: '5px' }}
									onClick={() => onCreateControl('knob')}
									size="large"
								>
									<SvgIcon>
										<path
											// eslint-disable-next-line max-len
											d="M22,15C22,17.6 20.8,19.9 18.9,21.3L18.4,20.8L16.3,18.7L17.7,17.3L18.9,18.5C19.4,17.8 19.8,16.9 19.9,16H18V14H19.9C19.7,13.1 19.4,12.3 18.9,11.5L17.7,12.7L16.3,11.3L17.5,10.1C16.8,9.6 15.9,9.2 15,9.1V11H13V9.1C12.1,9.3 11.3,9.6 10.5,10.1L13.5,13.1C13.7,13.1 13.8,13 14,13A2,2 0 0,1 16,15A2,2 0 0,1 14,17A2,2 0 0,1 12,15C12,14.8 12,14.7 12.1,14.5L9.1,11.5C8.6,12.2 8.2,13.1 8.1,14H10V16H8.1C8.3,16.9 8.6,17.7 9.1,18.5L10.3,17.3L11.7,18.7L9.1,21.3C7.2,19.9 6,17.6 6,15A8,8 0 0,1 14,7A8,8 0 0,1 22,15"
										/>
									</SvgIcon>
								</IconButton>
							</Tooltip>
						</ImageListItem>
					</ImageList>
				</Popover>
			)}
			<Tooltip
				key="ttChart"
				enterDelay={300}
				title={<FormattedMessage id="Tooltip.InsertChart" defaultMessage="Show Chart Types" />}
			>
				<div>
					<IconButton
						style={buttonStyle}
						onClick={(event) => onShowPopup(event, 'charts')}
						disabled={selectionInfo.type === '' || !appEditable}
						size="large"
					>
						<SvgIcon>
							<path d="M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z" />
						</SvgIcon>
					</IconButton>
				</div>
			</Tooltip>
			{popupId !== 'charts' ? null : (
				<Popover
					key="popCharts"
					open={popupId === 'charts'}
					anchorEl={anchorEl}
					anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
					transformOrigin={{ horizontal: 'right', vertical: 'top' }}
					onClose={onClosePopup}
					style={{
						overflow: 'hidden'
					}}
					TransitionProps={{
						onExited: handleFocus
					}}
				>
					<ImageList
						cols={6}
						rowHeight={40}
						gap={4}
						style={{
							width: '270px',
							margin: '1px'
						}}
					>
						<ImageListItem
							cols={6}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="StreamChart.TypesColumn" defaultMessage="Bar and Column Charts" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Column" defaultMessage="Column" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('column')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/column.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={
									<FormattedMessage id="StreamChart.ColumnStacked" defaultMessage="Column Stacked" />
								}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('columnstacked')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/columnstacked.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={
									<FormattedMessage
										id="StreamChart.ColumnStacked100"
										defaultMessage="Column Stacked 100"
									/>
								}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('columnstacked100')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/columnstacked100.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Bar" defaultMessage="Bar" />}
							>
								<IconButton style={chartButtonStyle} onClick={() => onCreatePlot('bar')} size="large">
									<img alt="" style={chartImageStyle} src="images/charts/bar.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.BarStacked" defaultMessage="Bar Stacked" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('barstacked')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/barstacked.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={
									<FormattedMessage id="StreamChart.BarStacked100" defaultMessage="Bar Stacked 100" />
								}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('barstacked100')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/barstacked100.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem
							cols={6}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="StreamChart.TypesLine" defaultMessage="Line Charts" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Line" defaultMessage="Line" />}
							>
								<IconButton style={chartButtonStyle} onClick={() => onCreatePlot('line')} size="large">
									<img alt="" style={chartImageStyle} src="images/charts/line.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.LineStacked" defaultMessage="Line Stacked" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('linestacked')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/linestacked.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={
									<FormattedMessage
										id="StreamChart.LineStacked100"
										defaultMessage="Line Stacked 100"
									/>
								}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('linestacked100')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/linestacked100.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Step" defaultMessage="Line Step" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('linestep')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/linestep.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Profile" defaultMessage="Profile" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('profile')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/profile.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem
							cols={6}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="StreamChart.TypesArea" defaultMessage="Area Charts" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Area" defaultMessage="Area" />}
							>
								<IconButton style={chartButtonStyle} onClick={() => onCreatePlot('area')} size="large">
									<img alt="" style={chartImageStyle} src="images/charts/area.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.AreaStacked" defaultMessage="Area Stacked" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('areastacked')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/areastacked.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={
									<FormattedMessage
										id="StreamChart.AreaStacked100"
										defaultMessage="Area Stacked 100"
									/>
								}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('areastacked100')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/areastacked100.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem
							cols={6}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="StreamChart.TypesXY" defaultMessage="XY Charts" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Scatter" defaultMessage="Scatter" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('scattermarker')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/scattermarker.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={
									<FormattedMessage
										id="StreamChart.ScatterLineMarker"
										defaultMessage="Scatter with Line and Markers"
									/>
								}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('scatterlinemarker')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/scatterlinemarker.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={
									<FormattedMessage id="StreamChart.ScatterLine" defaultMessage="Scatter with Line" />
								}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('scatterline')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/scatterline.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Bubble" defaultMessage="Bubble" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('bubble')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/bubble.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Heatmap" defaultMessage="Heatmap" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('heatmap')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/heatmap.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem
							cols={6}
							style={{
								height: '24px'
							}}
						>
							<div style={gridTitleStyle}>
								<FormattedMessage id="StreamChart.TypesPie" defaultMessage="Pie and Doughnut Charts" />
							</div>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Pie" defaultMessage="Pie" />}
							>
								<IconButton style={chartButtonStyle} onClick={() => onCreatePlot('pie')} size="large">
									<img alt="" style={chartImageStyle} src="images/charts/pie.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.HalfPie" defaultMessage="Half Pie" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('piehalf')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/halfpie.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.3dPie" defaultMessage="3D Pie" />}
							>
								<IconButton style={chartButtonStyle} onClick={() => onCreatePlot('pie3d')} size="large">
									<img alt="" style={chartImageStyle} src="images/charts/pie3d.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
						<ImageListItem cols={1}>
							<Tooltip
								enterDelay={300}
								title={<FormattedMessage id="StreamChart.Doughnut" defaultMessage="Doughnut" />}
							>
								<IconButton
									style={chartButtonStyle}
									onClick={() => onCreatePlot('doughnut')}
									size="large"
								>
									<img alt="" style={chartImageStyle} src="images/charts/doughnut.svg" />
								</IconButton>
							</Tooltip>
						</ImageListItem>
					</ImageList>
					<ChartExtensions
						titleColor={gridTitleColor}
						titleFontColor={gridTitleFontColor}
						onCreatePlot={onCreatePlot}
					/>
				</Popover>
			)}
			{mediaSize !== 'large'
				? null
				: [
						<Tooltip
							key="ttSelectShapes"
							enterDelay={300}
							title={<FormattedMessage id="Tooltip.SelectShapes" defaultMessage="Select Shapes" />}
						>
							<div>
								<IconButton
									onClick={onSelectShapes}
									disabled={!appEditable}
									style={buttonStyle}
									size="large"
								>
									<SvgIcon>
										<g>
											<path d="M11,17V19H9V17H11M7,17V19H5V17H7M19,9V11H17V9H19M19,5V7H17V5H19M15,5V7H13V5H15M11,5V7H9V5H11M7,5V7H5V5H7M7,13V15H5V13H7M7,9V11H5V9H7Z" />
											<path
												transform="translate(10 10) scale(0.6 0.6)"
												d="M10.07,14.27C10.57,14.03 11.16,14.25 11.4,14.75L13.7,19.74L15.5,18.89L13.19,13.91C12.95,13.41 13.17,12.81 13.67,12.58L13.95,12.5L16.25,12.05L8,5.12V15.9L9.82,14.43L10.07,14.27M13.64,21.97C13.14,22.21 12.54,22 12.31,21.5L10.13,16.76L7.62,18.78C7.45,18.92 7.24,19 7,19A1,1 0 0,1 6,18V3A1,1 0 0,1 7,2C7.24,2 7.47,2.09 7.64,2.23L7.65,2.22L19.14,11.86C19.57,12.22 19.62,12.85 19.27,13.27C19.12,13.45 18.91,13.57 18.7,13.61L15.54,14.23L17.74,18.96C18,19.46 17.76,20.05 17.26,20.28L13.64,21.97Z"
											/>
										</g>
									</SvgIcon>
								</IconButton>
							</div>
						</Tooltip>
				  ]}
			<ToolSeparator key="ts9" left />
			<Tooltip
				key="ttSheetSettings"
				enterDelay={300}
				title={<FormattedMessage id="Tooltip.SheetSettings" defaultMessage="Sheet Settings" />}
			>
				<div>
					<IconButton
						onClick={() => props.showDialog('SheetSettings')}
						disabled={!appEditable}
						style={buttonStyle}
						size="large"
					>
						<SvgIcon>
							<path d="M3 3H17C18.11 3 19 3.9 19 5V12.08C17.45 11.82 15.92 12.18 14.68 13H11V17H12.08C11.97 17.68 11.97 18.35 12.08 19H3C1.9 19 1 18.11 1 17V5C1 3.9 1.9 3 3 3M3 7V11H9V7H3M11 7V11H17V7H11M3 13V17H9V13H3M22.78 19.32L21.71 18.5C21.73 18.33 21.75 18.17 21.75 18S21.74 17.67 21.71 17.5L22.77 16.68C22.86 16.6 22.89 16.47 22.83 16.36L21.83 14.63C21.77 14.5 21.64 14.5 21.5 14.5L20.28 15C20 14.82 19.74 14.65 19.43 14.53L19.24 13.21C19.23 13.09 19.12 13 19 13H17C16.88 13 16.77 13.09 16.75 13.21L16.56 14.53C16.26 14.66 15.97 14.82 15.71 15L14.47 14.5C14.36 14.5 14.23 14.5 14.16 14.63L13.16 16.36C13.1 16.47 13.12 16.6 13.22 16.68L14.28 17.5C14.26 17.67 14.25 17.83 14.25 18S14.26 18.33 14.28 18.5L13.22 19.32C13.13 19.4 13.1 19.53 13.16 19.64L14.16 21.37C14.22 21.5 14.35 21.5 14.47 21.5L15.71 21C15.97 21.18 16.25 21.35 16.56 21.47L16.75 22.79C16.77 22.91 16.87 23 17 23H19C19.12 23 19.23 22.91 19.25 22.79L19.44 21.47C19.74 21.34 20 21.18 20.28 21L21.5 21.5C21.64 21.5 21.77 21.5 21.84 21.37L22.84 19.64C22.9 19.53 22.87 19.4 22.78 19.32M18 19.5C17.17 19.5 16.5 18.83 16.5 18S17.18 16.5 18 16.5 19.5 17.17 19.5 18 18.84 19.5 18 19.5Z" />{' '}
						</SvgIcon>
					</IconButton>
				</div>
			</Tooltip>
			<div
				style={{
					right: '10px',
					position: 'absolute'
				}}
			>
				<Tooltip
					key="ttArrange"
					enterDelay={300}
					title={<FormattedMessage id="Tooltip.ArrangeSheets" defaultMessage="Arrange StreamSheets" />}
				>
					<IconButton style={buttonStyle} onClick={onSplitView} size="large">
						<SvgIcon>
							{props.splitView ? (
								<path d="M3,4h18v17h-18v-17" />
							) : (
								<path d="M3,4h8v17h-8v-17 M14,4h8v17h-8v-17" />
							)}
						</SvgIcon>
					</IconButton>
				</Tooltip>
				<ToolbarExtensions />
			</div>
		</AppBar>
	);
}
